import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Phone, Mail, MapPin, Heart, Home, Key, Layers, 
  Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Building, 
  Users, CheckCircle, Compass, FileText, 
  Handshake, Map, Check, Clock, Loader2, MessageSquare,
  Star, Sparkles, X, SlidersHorizontal
} from 'lucide-react';

import { Apartment, Agent, Service, FAQ } from './types';
import { 
  INITIAL_AGENTS, 
  INITIAL_SERVICES, 
  INITIAL_FAQS, 
  POPULAR_NEIGHBORHOODS,
  STOCK_GALLERY_IMAGES 
} from './data/initialData';
import { AGENCY_CONFIG } from './config/agency';
import { DEFAULT_AGENTS_FROM_BROKERS } from './data/estateBrokers';
import { ALL_PROPERTY_CATEGORY_NAMES } from './data/categories';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PropertyCard from './components/PropertyCard';
import SearchFilters, { FilterState } from './components/SearchFilters';
import PropertyDetailModal from './components/PropertyDetailModal';
import GoogleReviewsCarousel from './components/GoogleReviewsCarousel';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/admin/AdminLogin';
import HeadSEO from './components/HeadSEO';
import { LegalPage } from './components/legal/LegalPage';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { CookieSettingsTrigger } from './components/CookieSettingsTrigger';
import { initConsentMode, syncTrackingScripts, trackPixelEvent } from './services/cookieConsentService';
import { safeString, sanitizeApartments } from './utils/safeTypes';
import { getPositionedImageStyle } from './utils/imageStyle';
import { isVideoUrl, formatMediaUrl } from './utils/mediaUtils';

import { useSiteContent } from './context/SiteContentContext';
import { useAuth } from './context/AuthContext';
import { auth } from './config/firebase';
import { 
  getAgentsFromFirebase,
  saveAgentToFirebase,
  deleteAgentFromFirebase,
  saveAgentsOrderToFirebase,
  submitInquiryToFirebase
} from './services/firebaseService';
import { sendClientInquiry } from './services/inquiryService';

const DEFAULT_NEIGHBORHOOD_IMAGES: Record<string, string> = {
  'Център': 'https://images.unsplash.com/photo-1577495508048-b635879837f1?w=600&q=80',
  'Бриз': 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80',
  'Чайка': 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80',
  'Гръцка махала': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80',
  'Левски': 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80',
  'ВИНС': 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80',
  'Колхозен пазар': 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80',
  'Възраждане': 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&q=80',
  'Победа': 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600&q=80',
  'м-т Ален Мак': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
  'Виница': 'https://images.unsplash.com/photo-1512915922686-57c11dde9b6b?w=600&q=80',
  'Трошево': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&q=80',
  'Цветен квартал': 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=600&q=80',
  'Аспарухово': 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&q=80',
  'Св. Св. Константин и Елена': 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=600&q=80',
  'Златни пясъци': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
};

export default function App() {
  const { content, updateSection, saveContent } = useSiteContent();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('home');
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);

  // Initialize European GDPR / ePrivacy Cookie Consent Mode v2 & Sync Tracking
  useEffect(() => {
    initConsentMode();
    syncTrackingScripts(content.seo?.tracking);
  }, [content.seo?.tracking]);
  
  // MODALS
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // INQUIRY FORM STATES
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: 'buy',
    message: ''
  });

  const [isConsultationSubmitting, setIsConsultationSubmitting] = useState(false);
  const [consultationFormData, setConsultationFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: 'now',
    interest: 'buy'
  });

  // DATA STATE
  const [apartments, setApartments] = useState<Apartment[]>(() => {
    const saved = localStorage.getItem('varna_imoti_apartments');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Keep only pure manual additions created offline (never resurrect deleted/archived API properties)
          const filtered = parsed.filter(a => !a.syncedFromApi && !a.estateId && !String(a.id).startsWith('ea-') && a.isCustom);
          return sanitizeApartments(filtered);
        }
      } catch (e) {
        console.error("Failed parsing custom properties:", e);
      }
    }
    return [];
  });

  const apartmentsRef = useRef(apartments);
  useEffect(() => {
    apartmentsRef.current = apartments;
  }, [apartments]);

  // Featured property selected specifically for Hero section
  const heroFeaturedProperty = useMemo(() => {
    if (content.hero?.featuredPropertyId) {
      const targetId = String(content.hero.featuredPropertyId).trim();
      const numOnly = targetId.replace(/\D/g, '');
      const match = apartments.find(a => 
        (a.isActive !== false) && (
          String(a.id) === targetId || 
          String(a.estateId) === targetId ||
          String((a as any).code) === targetId ||
          (numOnly && String(a.id).replace(/\D/g, '') === numOnly) ||
          (numOnly && String(a.estateId).replace(/\D/g, '') === numOnly)
        )
      );
      if (match) return match;
    }
    return apartments.find(a => a.isActive !== false) || null;
  }, [apartments, content.hero?.featuredPropertyId]);

  const handleOpenApartment = (property: Apartment) => {
    setSelectedApartment(property);
    const targetPath = `/imoti/${encodeURIComponent(property.id)}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
    trackPixelEvent('ViewContent', {
      content_name: property.title,
      content_ids: [property.id],
      content_type: 'product',
      value: property.price,
      currency: 'EUR'
    });
  };

  const handleCloseApartment = () => {
    setSelectedApartment(null);
    if (window.location.pathname.startsWith('/imoti/')) {
      window.history.pushState(null, '', '/');
    }
  };

  const [favorites, setFavorites] = useState<string[]>(() => {
    const saved = localStorage.getItem('varna_imoti_favorites');
    return saved ? JSON.parse(saved) : [];
  });

  const [agents, setAgents] = useState<Agent[]>(() => {
    const saved = localStorage.getItem('varna_imoti_agents');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsed.sort((a, b) => (Number(a.order ?? 999999) - Number(b.order ?? 999999)));
          return parsed;
        }
      } catch (e) {
        console.error("Failed parsing agents:", e);
      }
    }
    return DEFAULT_AGENTS_FROM_BROKERS;
  });

  // FILTERS STATE
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    type: 'all',
    neighborhood: '',
    propertyType: '',
    priceMin: '',
    priceMax: '',
    areaMin: '',
    areaMax: '',
    rooms: '',
    searchWord: '',
    sortBy: 'newest'
  });

  // Selected Category filter for Team Page
  const [teamCategory, setTeamCategory] = useState<'all' | 'sales' | 'rentals' | 'office'>('all');

  // FAQ Accordion Active Index
  const [activeFaqIdx, setActiveFaqIdx] = useState<number | null>(0);

  // Pagination State for high-performance browsing
  const [salesPage, setSalesPage] = useState<number>(1);
  const [rentalsPage, setRentalsPage] = useState<number>(1);
  const PAGE_SIZE = 24;

  useEffect(() => {
    setSalesPage(1);
    setRentalsPage(1);
  }, [activeFilters, activeTab]);

  // Sync to local storage safely without saving API synced items
  useEffect(() => {
    try {
      const customOnly = apartments.filter(a => !a.syncedFromApi && !a.estateId && !String(a.id).startsWith('ea-') && a.isCustom);
      localStorage.setItem('varna_imoti_apartments', JSON.stringify(customOnly));
    } catch (e) {
      console.warn('LocalStorage quota limit reached:', e);
    }
  }, [apartments]);

  useEffect(() => {
    localStorage.setItem('varna_imoti_favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem('varna_imoti_agents', JSON.stringify(agents));
  }, [agents]);

  // Handle URL route changes for /admin, deep-linked properties (/imoti/{id}), and section hashes
  useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname.toLowerCase().replace(/\/$/, '');
      const hash = window.location.hash.toLowerCase();

      // Deep linking: /imoti/{id}
      if (path.startsWith('/imoti/')) {
        const rawId = decodeURIComponent(path.slice('/imoti/'.length)).trim();
        if (rawId) {
          const match = apartmentsRef.current.find(a => 
            String(a.id) === rawId || 
            String(a.estateId) === rawId || 
            `ea-${a.estateId}` === rawId
          );
          if (match) {
            setSelectedApartment(match);
          }
        }
      } else {
        // If navigating away from /imoti/ via browser back/forward, close property modal
        setSelectedApartment(prev => (prev ? null : prev));
      }

      if (path === '/admin' || path.startsWith('/admin') || hash === '#admin') {
        setActiveTab('admin');
      } else if (path === '/oferti' || hash === '#oferti' || hash === '#all') {
        setActiveTab('all');
        setActiveFilters(prev => ({ ...prev, type: 'all' }));
      } else if (path === '/prodazhbi' || path === '/sales' || hash === '#prodazhbi' || hash === '#sales') {
        setActiveTab('sales');
        setActiveFilters(prev => ({ ...prev, type: 'sale' }));
      } else if (path === '/naemi' || path === '/rentals' || hash === '#naemi' || hash === '#rentals') {
        setActiveTab('rentals');
        setActiveFilters(prev => ({ ...prev, type: 'rent' }));
      } else if (path === '/uslugi' || path === '/services' || hash === '#uslugi' || hash === '#services') {
        setActiveTab('services');
      } else if (path === '/ekip' || path === '/team' || hash === '#ekip' || hash === '#team') {
        setActiveTab('team');
      } else if (path === '/kontakti' || path === '/contacts' || hash === '#kontakti' || hash === '#contacts') {
        setActiveTab('contacts');
      } else if (path === '/za-nas' || path === '/about' || hash === '#za-nas' || hash === '#about') {
        setActiveTab('about');
      } else if (path === '/obshti-uslovia' || path === '/terms' || hash === '#terms' || hash === '#obshti-uslovia') {
        setActiveTab('terms');
      } else if (path === '/poveritelnost' || path === '/privacy' || hash === '#privacy' || hash === '#poveritelnost') {
        setActiveTab('privacy');
      } else if (path === '' || path === '/') {
        setActiveTab(prev => (prev === 'admin' ? 'home' : prev));
      }
    };

    handleLocationChange();
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  // Auto-open modal if initial URL is /imoti/{id} when apartments finish loading from API/KV
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/imoti/') && apartments.length > 0) {
      const rawId = decodeURIComponent(path.slice('/imoti/'.length)).trim();
      if (rawId) {
        const match = apartments.find(a => 
          (a.isActive !== false) && (
            String(a.id) === rawId || 
            String(a.estateId) === rawId || 
            `ea-${a.estateId}` === rawId
          )
        );
        if (match) {
          setSelectedApartment(match);
        }
      }
    }
  }, [apartments]);

  // Fetch properties and agents from Cloudflare KV / Server API and Firebase Firestore
  const fetchServerProperties = async () => {
    try {
      // 1. Fetch properties from Cloudflare KV / Server API (/api/properties) with cache-busting
      const res = await fetch(`/api/properties?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
      });
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          const propList = Array.isArray(data) ? data : (data.properties || []);
          const sanitizedServer = sanitizeApartments(propList);
          
          setApartments(prev => {
            // Server is single source of truth for API properties.
            // Retain ONLY pure manual custom properties created locally that are NOT from API
            const localCustom = prev.filter(p => !p.syncedFromApi && !p.estateId && !String(p.id).startsWith('ea-') && p.isCustom);
            if (localCustom.length === 0) return sanitizedServer;

            const normId = (s?: any) => String(s || '').trim().replace(/^ea-/, '').toLowerCase();
            const merged = [...sanitizedServer];

            localCustom.forEach(c => {
              const exists = merged.some(m => normId(m.id) === normId(c.id));
              if (!exists) merged.unshift(c);
            });

            return merged;
          });
        }
      }
    } catch (apiErr: any) {
      if (apiErr?.name !== 'AbortError' && !apiErr?.message?.includes('aborted')) {
        console.warn('Properties API fetch fallback to local:', apiErr);
      }
    }

    try {
      // 2. Fetch agents from Firebase Firestore
      const fbAgents = await getAgentsFromFirebase();
      if (fbAgents && fbAgents.length > 0) {
        setAgents(fbAgents);
      }
    } catch (fbErr: any) {
      if (fbErr?.name !== 'AbortError' && !fbErr?.message?.includes('aborted')) {
        console.warn('Agents Firebase fetch fallback:', fbErr);
      }
    }
  };

  useEffect(() => {
    fetchServerProperties();

    // Auto-poll every 30 seconds to immediately reflect changes when agents archive or edit in CRM
    const interval = setInterval(fetchServerProperties, 30000);

    // Re-fetch immediately when user returns to this browser tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchServerProperties();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'admin') {
      if (window.location.pathname !== '/admin') {
        window.history.pushState(null, '', '/admin');
      }
    } else {
      const tabPaths: Record<string, string> = {
        home: '/',
        all: '/oferti',
        sales: '/prodazhbi',
        rentals: '/naemi',
        services: '/uslugi',
        team: '/ekip',
        contacts: '/kontakti',
        about: '/za-nas',
        terms: '/obshti-uslovia',
        privacy: '/poveritelnost',
      };
      const targetUrl = tabPaths[tab] || '/';
      const currentUrl = window.location.pathname;
      if (currentUrl !== targetUrl || window.location.hash) {
        window.history.pushState(null, '', targetUrl);
      }
    }

    // Clean reset of search filters on primary tab switch to ensure all listings are visible
    if (tab === 'sales') {
      setActiveFilters({
        type: 'sale',
        neighborhood: '',
        city: 'Варна',
        propertyType: '',
        priceMin: '',
        priceMax: '',
        areaMin: '',
        areaMax: '',
        rooms: '',
        searchWord: '',
        sortBy: 'newest'
      });
    } else if (tab === 'rentals') {
      setActiveFilters({
        type: 'rent',
        neighborhood: '',
        city: 'Варна',
        propertyType: '',
        priceMin: '',
        priceMax: '',
        areaMin: '',
        areaMax: '',
        rooms: '',
        searchWord: '',
        sortBy: 'newest'
      });
    } else if (tab === 'all') {
      setActiveFilters({
        type: 'all',
        neighborhood: '',
        city: 'Варна',
        propertyType: '',
        priceMin: '',
        priceMax: '',
        areaMin: '',
        areaMax: '',
        rooms: '',
        searchWord: '',
        sortBy: 'newest'
      });
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Seamless filter change handler that keeps activeTab and activeFilters in perfect sync
  const handleFilterChange = (newFilters: FilterState) => {
    if (newFilters.type === 'rent' && (activeTab === 'sales' || activeTab === 'all')) {
      setActiveTab('rentals');
      window.history.pushState(null, '', '/naemi');
    } else if (newFilters.type === 'sale' && (activeTab === 'rentals' || activeTab === 'all')) {
      setActiveTab('sales');
      window.history.pushState(null, '', '/prodazhbi');
    } else if (newFilters.type === 'all' && activeTab === 'rentals') {
      setActiveTab('sales');
      window.history.pushState(null, '', '/oferti');
    }
    setActiveFilters(newFilters);
  };

  // LISTINGS FILTER LOGIC
  const filteredApartments = apartments.filter(apt => {
    // Hide un-published / taken down properties in public view
    if (apt.isActive === false) return false;

    // 1. Transaction Type (Sale vs Rent vs All)
    // Priority: If user explicitly selected 'sale', 'rent', or 'all' in filters, respect that!
    // Otherwise fallback to page context (sales vs rentals vs all)
    const effectiveType: 'sale' | 'rent' | 'all' = 
      activeFilters.type 
        ? activeFilters.type 
        : (activeTab === 'rentals' ? 'rent' : (activeTab === 'sales' ? 'sale' : 'all'));

    if (effectiveType === 'sale' && apt.type !== 'sale') return false;
    if (effectiveType === 'rent' && apt.type !== 'rent') return false;

    const aptNeigh = safeString(apt.neighborhood);
    const aptLocation = safeString(apt.location);
    const aptRegion = safeString(apt.region);
    const aptTitle = safeString(apt.title);
    const aptId = safeString(apt.id);
    const aptEstateType = safeString(apt.estateType);

    // 2. City
    if (activeFilters.city && activeFilters.city !== 'Варна' && safeString(apt.city).toLowerCase() !== activeFilters.city.toLowerCase()) {
      return false;
    }

    // 3. Neighborhood
    if (activeFilters.neighborhood) {
      const targetNeigh = activeFilters.neighborhood.toLowerCase().trim();
      const matchesNeigh = aptNeigh.toLowerCase().trim() === targetNeigh ||
                           aptLocation.toLowerCase().trim() === targetNeigh ||
                           aptRegion.toLowerCase().trim() === targetNeigh ||
                           aptNeigh.toLowerCase().includes(targetNeigh) ||
                           aptLocation.toLowerCase().includes(targetNeigh) ||
                           aptTitle.toLowerCase().includes(targetNeigh);
      if (!matchesNeigh) return false;
    }

    // 4. Property Type, Plot & Garage Recognition
    if (activeFilters.propertyType) {
      const targetType = activeFilters.propertyType.toLowerCase().trim();
      const isPlotQuery = targetType.includes('парцел') || targetType.includes('упи') || targetType.includes('терен') || targetType.includes('земедел') || targetType.includes('нива');
      const isPlotApt = aptEstateType.toLowerCase().includes('парцел') || 
                        aptEstateType.toLowerCase().includes('упи') || 
                        aptEstateType.toLowerCase().includes('терен') || 
                        aptEstateType.toLowerCase().includes('земедел') || 
                        aptEstateType.toLowerCase().includes('нива') || 
                        aptEstateType.toLowerCase().includes('инвест');

      const isGarageQuery = targetType.includes('гараж') || targetType.includes('паркомяст');
      const isGarageApt = aptEstateType.toLowerCase().includes('гараж') || 
                          aptEstateType.toLowerCase().includes('паркомяст');

      if (isPlotQuery) {
        if (!isPlotApt) return false;
      } else if (isGarageQuery) {
        if (!isGarageApt) return false;
      } else {
        // If searching for regular properties/apartments/houses, exclude plots and garages!
        if (isPlotApt || isGarageApt) return false;

        const numMatch = targetType.match(/\d+/);
        const targetRooms = numMatch ? parseInt(numMatch[0], 10) : null;
        let matchesType = false;
        if (targetRooms !== null) {
          if (targetRooms >= 4) {
            matchesType = apt.rooms >= 4 || 
                          aptEstateType.toLowerCase().includes('4') || 
                          aptEstateType.toLowerCase().includes('многостаен') ||
                          aptTitle.toLowerCase().includes('4-стаен') ||
                          aptTitle.toLowerCase().includes('многостаен');
          } else {
            matchesType = apt.rooms === targetRooms || 
                          aptEstateType.toLowerCase().includes(String(targetRooms)) ||
                          aptEstateType.toLowerCase().includes(targetType) ||
                          aptTitle.toLowerCase().includes(targetType);
          }
        } else if (targetType.includes('къщ') || targetType.includes('вила')) {
          matchesType = (aptEstateType.toLowerCase().includes('къщ') || 
                         aptEstateType.toLowerCase().includes('вила') ||
                         aptTitle.toLowerCase().includes('къщ') ||
                         aptTitle.toLowerCase().includes('вила')) && !isGarageApt;
        } else {
          matchesType = aptEstateType.toLowerCase().includes(targetType) ||
                        aptTitle.toLowerCase().includes(targetType);
        }
        if (!matchesType) return false;
      }
    }

    // 5. Budget
    if (activeFilters.priceMin && apt.price < parseFloat(activeFilters.priceMin)) return false;
    if (activeFilters.priceMax && apt.price > parseFloat(activeFilters.priceMax)) return false;

    // 6. Area
    if (activeFilters.areaMin && apt.area < parseFloat(activeFilters.areaMin)) return false;
    if (activeFilters.areaMax && apt.area > parseFloat(activeFilters.areaMax)) return false;

    // 7. Number of rooms (plots and garages have 0 rooms and are excluded)
    if (activeFilters.rooms) {
      const isPlotOrGarage = aptEstateType.toLowerCase().includes('парцел') || aptEstateType.toLowerCase().includes('упи') || aptEstateType.toLowerCase().includes('гараж') || aptTitle.toLowerCase().includes('парцел');
      if (isPlotOrGarage) return false;
      const parsedRooms = parseInt(activeFilters.rooms, 10);
      if (parsedRooms === 4) {
        if (apt.rooms < 4) return false;
      } else {
        if (apt.rooms !== parsedRooms) return false;
      }
    }

    // 8. Text Search
    if (activeFilters.searchWord) {
      const query = activeFilters.searchWord.toLowerCase().trim();
      const matchesTitle = aptTitle.toLowerCase().includes(query);
      const matchesId = aptId.toLowerCase().includes(query);
      const matchesNeigh = aptNeigh.toLowerCase().includes(query);
      const matchesType = aptEstateType.toLowerCase().includes(query);
      if (!matchesTitle && !matchesId && !matchesNeigh && !matchesType) return false;
    }

    return true;
  }).sort((a, b) => {
    // SORTING LOGIC
    if (activeFilters.sortBy === 'price-asc') return a.price - b.price;
    if (activeFilters.sortBy === 'price-desc') return b.price - a.price;
    if (activeFilters.sortBy === 'area-desc') return b.area - a.area;
    if (activeFilters.sortBy === 'rooms-desc') return b.rooms - a.rooms;
    // Admin custom defined order
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    // default newest by Reference ID numeric or value
    const numB = parseInt(String(b.estateId || b.id).replace(/\D/g, '')) || 0;
    const numA = parseInt(String(a.estateId || a.id).replace(/\D/g, '')) || 0;
    return numB - numA;
  });

  // Paginated slices
  const totalSalesPages = Math.max(1, Math.ceil(filteredApartments.length / PAGE_SIZE));
  const paginatedSalesApartments = filteredApartments.slice((salesPage - 1) * PAGE_SIZE, salesPage * PAGE_SIZE);

  const totalRentalsPages = Math.max(1, Math.ceil(filteredApartments.length / PAGE_SIZE));
  const paginatedRentalsApartments = filteredApartments.slice((rentalsPage - 1) * PAGE_SIZE, rentalsPage * PAGE_SIZE);

  // Pagination UI Component
  const renderPagination = (currentPage: number, totalPages: number, setPage: (p: number) => void) => {
    if (totalPages <= 1) return null;

    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div className="flex flex-wrap justify-center items-center gap-1.5 sm:gap-2 mt-12 pt-6 border-t border-gray-100">
        <button 
          onClick={() => {
            setPage(Math.max(1, currentPage - 1));
            window.scrollTo({ top: 400, behavior: 'smooth' });
          }}
          disabled={currentPage === 1}
          className="px-3 py-1.5 rounded border border-gray-200 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          &larr; Предишна
        </button>
        {pages.map((p, idx) => typeof p === 'number' ? (
          <button
            key={idx}
            onClick={() => {
              setPage(p);
              window.scrollTo({ top: 400, behavior: 'smooth' });
            }}
            className={`min-w-[32px] px-2.5 py-1.5 rounded text-xs font-bold transition ${
              currentPage === p 
                ? 'bg-[#0F1E32] text-white shadow-sm' 
                : 'border border-gray-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {p}
          </button>
        ) : (
          <span key={idx} className="px-1 text-slate-400 text-xs">...</span>
        ))}
        <button 
          onClick={() => {
            setPage(Math.min(totalPages, currentPage + 1));
            window.scrollTo({ top: 400, behavior: 'smooth' });
          }}
          disabled={currentPage === totalPages}
          className="px-3 py-1.5 rounded border border-gray-200 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          Следваща &rarr;
        </button>
      </div>
    );
  };

  // Extracted unique options for search filters & admin panel
  const uniqueNeighborhoods: string[] = useMemo(() => {
    const set = new Set<string>();
    apartments.forEach(a => {
      const n = safeString(a.neighborhood).trim();
      if (n) set.add(n);
    });
    (content.footer.popularLocations || []).forEach(loc => {
      const n = safeString(loc.neighborhood).trim();
      if (n) set.add(n);
    });
    // Add common Varna neighborhoods and regional settlements so dropdown is always rich
    [
      'Център', 'Бриз', 'Чайка', 'Левски', 'Гръцка махала', 'Колхозен пазар', 
      'Възраждане', 'Победа', 'м-т Ален Мак', 'Виница', 'Трошево', 
      'Цветен квартал', 'Аспарухово', 'Св. Св. Константин и Елена', 'Златни пясъци',
      'с. Слънчево', 'с. Приселци', 'с. Звездица', 'с. Близнаци', 'с. Кичево', 'с. Осеново', 'с. Тополи', 'с. Константиново', 'гр. Белослав', 'гр. Девня', 'гр. Бяла'
    ].forEach(n => set.add(n));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'bg'));
  }, [apartments, content.footer.popularLocations]);

  const uniquePropertyTypes = useMemo(() => {
    const fromProps = apartments.map(a => a.estateType).filter(Boolean) as string[];
    return Array.from(new Set([...ALL_PROPERTY_CATEGORY_NAMES, ...fromProps]));
  }, [apartments]);

  // Handler to filter properties by clicked popular location/neighborhood
  const handleSelectPopularNeighborhood = (neighborhoodName: string) => {
    const cleanNeigh = (neighborhoodName || '').trim();
    if (!cleanNeigh) return;

    setActiveFilters(prev => ({
      ...prev,
      type: 'all', // Show all offers (both sales and rentals) in this neighborhood
      neighborhood: cleanNeigh,
      searchWord: '',
      city: 'Варна',
    }));

    // Switch tab to 'all' catalog view
    setActiveTab('all');
    setSalesPage(1);
    setRentalsPage(1);
    window.history.pushState(null, '', '/oferti');

    // Smooth scroll to catalog results
    setTimeout(() => {
      const target = document.getElementById('search-filters-container') || 
                     document.getElementById('catalog-results-anchor') || 
                     document.getElementById('brand-logo');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 400, behavior: 'smooth' });
      }
    }, 80);
  };

  // Reusable Popular Regions / Locations Section
  const renderPopularRegionsSection = () => {
    const configured = content.footer.popularLocations || [];
    const items = configured.length > 0 
      ? configured.map(loc => {
          const neigh = loc.neighborhood || loc.name;
          return {
            name: loc.name,
            neighborhood: neigh,
            image: loc.image || DEFAULT_NEIGHBORHOOD_IMAGES[neigh] || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80'
          };
        })
      : POPULAR_NEIGHBORHOODS.map(n => ({
          name: `Имоти в ${n.name}`,
          neighborhood: n.name,
          image: n.image
        }));

    return (
      <div className="bg-slate-100 py-16 px-4 mt-16 rounded-2xl border border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">РАЙОНИ ВЪВ ВАРНА</span>
            <h3 className="text-2xl md:text-3xl font-black text-[#0F1E32] mt-1 uppercase">
              {content.footer.popularLocationsTitle || 'Популярни райони за имоти'}
            </h3>
            <p className="text-xs text-slate-600 mt-1.5 max-w-xl mx-auto">
              Селектирайте един от популярните квартали на Варна, за да разгледате всички актуални оферти в този район веднага.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {items.map((item, idx) => {
              const realCount = apartments.filter(a => {
                const an = (a.neighborhood || '').toLowerCase().trim();
                const locN = item.neighborhood.toLowerCase().trim();
                return an === locN || an.includes(locN) || locN.includes(an);
              }).length;

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectPopularNeighborhood(item.neighborhood)}
                  className="group relative rounded-xl overflow-hidden aspect-square shadow hover:shadow-xl hover:-translate-y-0.5 transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#C5A46E]/50 cursor-pointer"
                  title={`Виж всички имоти в ${item.neighborhood}`}
                >
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-300"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F1E32] via-[#0F1E32]/70 to-transparent flex flex-col justify-end p-3.5">
                    <span className="text-white font-extrabold text-xs sm:text-sm font-sans tracking-tight line-clamp-1">
                      {item.neighborhood}
                    </span>
                    <span className="text-[10px] text-[#C5A46E] font-semibold uppercase mt-0.5">
                      {realCount > 0 ? `${realCount} ${realCount === 1 ? 'ОФЕРТА' : 'ОФЕРТИ'}` : 'ВИЖ ИМОТИ'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // HANDLERS
  const handleToggleFavorite = (id: string) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(favId => favId !== id) : [...prev, id]
    );
  };

  const handleAddProperty = async (property: Apartment) => {
    setApartments(prev => [property, ...prev]);
    triggerSuccess('Имотът беше успешно добавен и публикуван в сайта!');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      await fetch('/api/properties', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify(property)
      });
    } catch (e) {
      console.warn('Failed to persist property to /api/properties:', e);
    }
  };

  const handleEditProperty = async (updatedProperty: Apartment) => {
    const normId = (s?: any) => String(s || '').trim().replace(/^ea-/, '').toLowerCase();
    const targetIdNorm = normId(updatedProperty.id);
    const targetEstateIdNorm = normId(updatedProperty.estateId);
    const targetCodeNorm = normId(updatedProperty.code);

    setApartments(prev => {
      const matchIdx = prev.findIndex(a => {
        const aIdNorm = normId(a.id);
        const aEstateIdNorm = normId(a.estateId);
        const aCodeNorm = normId(a.code);
        return (
          a.id === updatedProperty.id ||
          (targetIdNorm && aIdNorm === targetIdNorm) ||
          (targetEstateIdNorm && (aEstateIdNorm === targetEstateIdNorm || aIdNorm === targetEstateIdNorm)) ||
          (targetCodeNorm && (aCodeNorm === targetCodeNorm || aIdNorm === targetCodeNorm))
        );
      });

      const finalItem: Apartment = {
        ...(matchIdx >= 0 ? prev[matchIdx] : {}),
        ...updatedProperty,
        isCustom: true,
        isModifiedByAdmin: true,
        isActive: true
      };

      if (matchIdx >= 0) {
        const copy = [...prev];
        copy[matchIdx] = finalItem;
        return copy;
      } else {
        return [finalItem, ...prev];
      }
    });

    triggerSuccess('Офертата беше успешно обновена!');

    try {
      const idToken = await auth.currentUser?.getIdToken();
      await fetch('/api/properties', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({
          ...updatedProperty,
          isCustom: true,
          isModifiedByAdmin: true,
          isActive: true
        })
      });
    } catch (e) {
      console.warn('Failed to persist updated property to /api/properties:', e);
    }
  };

  const handleTogglePropertyStatus = async (id: string) => {
    // По изискване на клиента: „значи искам като бъде различна от активна да се маха изцяло от сайта и от админ панела!“
    await handleDeleteProperty(id);
  };

  const handleReorderProperties = async (reordered: Apartment[]) => {
    const updated = reordered.map((item, idx) => ({ ...item, order: idx + 1 }));
    setApartments(updated);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      await fetch('/api/properties/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({ orderList: updated.map(p => ({ id: p.id, estateId: p.estateId, order: p.order })) })
      });
    } catch (e) {
      console.warn('Could not persist reordered properties:', e);
    }
    triggerSuccess('Подредбата на обявите беше успешно запазена!');
  };

  const handleDeleteProperty = async (id: string) => {
    const normId = (s?: any) => String(s || '').trim().replace(/^ea-/, '').toLowerCase();
    const targetIdNorm = normId(id);

    setApartments(prev => prev.filter(a => {
      const aIdNorm = normId(a.id);
      const aEstateIdNorm = normId(a.estateId);
      const aCodeNorm = normId(a.code);
      return !(
        a.id === id ||
        (targetIdNorm && (aIdNorm === targetIdNorm || aEstateIdNorm === targetIdNorm || aCodeNorm === targetIdNorm))
      );
    }));

    triggerSuccess('Офертата беше свалена и премахната успешно от сайта и админ панела.');

    try {
      const idToken = await auth.currentUser?.getIdToken();
      await fetch(`/api/properties?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({ id })
      });
      await fetch(`/api/properties/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: {
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        }
      }).catch(() => {});
    } catch (e) {
      console.warn('Failed to delete property from /api/properties:', e);
    }
  };

  const handleResetProperties = async () => {
    setApartments([]);
    localStorage.removeItem('varna_imoti_apartments');
    triggerSuccess('Всички имоти бяха изчистени от базата данни.');
    try {
      await fetch('/api/properties/clear', {
        method: 'POST'
      });
    } catch (e) {}
  };

  const handleAddAgent = async (agent: Agent) => {
    let updatedList: Agent[] = [];
    setAgents(prev => {
      const copy = [...prev];
      const targetOrder = agent.order !== undefined && agent.order > 0 ? Number(agent.order) : copy.length + 1;
      const insertIdx = Math.max(0, Math.min(targetOrder - 1, copy.length));
      copy.splice(insertIdx, 0, agent);
      updatedList = copy.map((a, idx) => ({ ...a, order: idx + 1 }));
      return updatedList;
    });
    triggerSuccess('Служителят беше успешно добавен!');
    try {
      await saveAgentToFirebase({ ...agent, order: agent.order || 1 });
      if (updatedList.length > 0) {
        await saveAgentsOrderToFirebase(updatedList);
      }
    } catch (fbErr) {
      console.warn('Firebase agent save fallback:', fbErr);
    }
  };

  const handleEditAgent = async (updatedAgent: Agent) => {
    let updatedList: Agent[] = [];
    setAgents(prev => {
      const remaining = prev.filter(a => a.id !== updatedAgent.id);
      const targetOrder = updatedAgent.order !== undefined && updatedAgent.order > 0 
        ? Number(updatedAgent.order) 
        : (prev.findIndex(a => a.id === updatedAgent.id) + 1 || remaining.length + 1);
      const insertIdx = Math.max(0, Math.min(targetOrder - 1, remaining.length));
      remaining.splice(insertIdx, 0, updatedAgent);
      updatedList = remaining.map((a, idx) => ({ ...a, order: idx + 1 }));
      return updatedList;
    });
    triggerSuccess('Данните и поредността на служителя бяха успешно обновени!');
    try {
      await saveAgentToFirebase(updatedAgent);
      if (updatedList.length > 0) {
        await saveAgentsOrderToFirebase(updatedList);
      }
    } catch (fbErr) {
      console.warn('Firebase agent update fallback:', fbErr);
    }
  };

  const handleDeleteAgent = async (id: string) => {
    let remainingList: Agent[] = [];
    setAgents(prev => {
      remainingList = prev.filter(a => a.id !== id).map((a, idx) => ({ ...a, order: idx + 1 }));
      return remainingList;
    });
    triggerSuccess('Служителят беше премахнат успешно.');
    try {
      await deleteAgentFromFirebase(id);
      if (remainingList.length > 0) {
        await saveAgentsOrderToFirebase(remainingList);
      }
    } catch (fbErr) {
      console.warn('Firebase agent delete fallback:', fbErr);
    }
  };

  const handleReorderAgents = async (newOrderedAgents: Agent[]) => {
    const indexed = newOrderedAgents.map((agent, index) => ({
      ...agent,
      order: index + 1
    }));
    setAgents(indexed);
    triggerSuccess('Редът на показване на екипа беше запазен успешно!');
    try {
      await saveAgentsOrderToFirebase(indexed);
    } catch (fbErr) {
      console.warn('Firebase batch order save fallback:', fbErr);
    }
  };

  const handleResetAgents = () => {
    setAgents([]);
    localStorage.removeItem('varna_imoti_agents');
    triggerSuccess('Списъкът със служители беше изчистен.');
  };

  const triggerSuccess = (message: string) => {
    setSuccessMessage(message);
    setIsSuccessModalOpen(true);
    setTimeout(() => {
      setIsSuccessModalOpen(false);
    }, 4500);
  };

  // Lead capture forms submits
  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consultationFormData.name.trim() || !consultationFormData.phone.trim()) {
      return;
    }
    setIsConsultationSubmitting(true);
    try {
      const interestLabels: Record<string, string> = {
        buy: 'Покупка на апартамент',
        sell: 'Продажба на собствен имот',
        rent: 'Дългосрочен наем / Наемане',
        landlord: 'Отдаване под наем',
        credit: 'Ипотечен кредит / Оценка'
      };
      const timeLabels: Record<string, string> = {
        now: 'Възможно най-рано',
        morning: 'Сутрин (9:00 - 12:00)',
        afternoon: 'Обедно време (12:00 - 14:00)',
        evening: 'След обяд (14:00 - 18:00)'
      };

      await sendClientInquiry({
        name: consultationFormData.name.trim(),
        phone: consultationFormData.phone.trim(),
        email: consultationFormData.email.trim() || undefined,
        preferredTime: timeLabels[consultationFormData.preferredTime] || consultationFormData.preferredTime,
        serviceType: interestLabels[consultationFormData.interest] || consultationFormData.interest,
        message: `Заявена безплатна консултация: ${interestLabels[consultationFormData.interest] || consultationFormData.interest}. Удобно време: ${timeLabels[consultationFormData.preferredTime] || consultationFormData.preferredTime}.${consultationFormData.email.trim() ? ` Имейл за обратна връзка: ${consultationFormData.email.trim()}` : ''}`,
        source: 'consultation_modal'
      });

      setIsConsultationOpen(false);
      trackPixelEvent('Lead', {
        content_name: 'Безплатна Консултация',
        interest: consultationFormData.interest
      });
      setConsultationFormData({
        name: '',
        phone: '',
        email: '',
        preferredTime: 'now',
        interest: 'buy'
      });
      triggerSuccess('Благодарим Ви! Вашето запитване беше изпратено директно по имейла на фирмата. Наш топ консултант ще се свърже с Вас!');
    } catch (err) {
      triggerSuccess('Запитването е регистрирано. Наш експерт ще се свърже с Вас!');
      setIsConsultationOpen(false);
    } finally {
      setIsConsultationSubmitting(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactFormData.name.trim() || !contactFormData.phone.trim()) {
      return;
    }
    setIsContactSubmitting(true);
    try {
      const typeLabels: Record<string, string> = {
        buy: 'Търся да закупя имот',
        sell: 'Продавам собствен имот',
        rent: 'Търся под наем',
        landlord: 'Отдавам под наем',
        consult: 'Друга консултация'
      };

      await sendClientInquiry({
        name: contactFormData.name.trim(),
        phone: contactFormData.phone.trim(),
        email: contactFormData.email.trim() || undefined,
        serviceType: typeLabels[contactFormData.serviceType] || contactFormData.serviceType,
        message: contactFormData.message.trim(),
        source: 'contact_page'
      });

      trackPixelEvent('Lead', {
        content_name: 'Запитване за контакт',
        serviceType: contactFormData.serviceType
      });

      setContactFormData({
        name: '',
        phone: '',
        email: '',
        serviceType: 'buy',
        message: ''
      });
      triggerSuccess('Благодарим Ви! Вашето съобщение беше изпратено успешно. Наш брокер ще се свърже с Вас възможно най-скоро.');
    } catch (err) {
      triggerSuccess('Съобщението беше записано. Очаквайте обаждане от наш брокер.');
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const hasActiveHomeFilters = useMemo(() => {
    return Boolean(
      (activeFilters.type && activeFilters.type !== 'all') ||
      activeFilters.neighborhood ||
      activeFilters.propertyType ||
      activeFilters.searchWord ||
      activeFilters.priceMin ||
      activeFilters.priceMax
    );
  }, [activeFilters]);

  const homeFilteredProperties = useMemo(() => {
    if (!hasActiveHomeFilters) return apartments.filter(a => a.isActive !== false);
    return filteredApartments;
  }, [hasActiveHomeFilters, apartments, filteredApartments]);

  const handleHeroSearch = () => {
    const targetTab = activeFilters.type === 'rent' ? 'rentals' : (activeFilters.type === 'all' ? 'all' : 'sales');
    setActiveTab(targetTab);
    const targetUrl = targetTab === 'rentals' ? '/naemi' : (targetTab === 'all' ? '/oferti' : '/prodazhbi');
    if (window.location.pathname !== targetUrl || window.location.hash) {
      window.history.pushState(null, '', targetUrl);
    }
    setTimeout(() => {
      const target = document.getElementById('catalog-results-anchor') || 
                     document.getElementById('properties-results-heading') ||
                     document.getElementById('featured-properties-heading');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 380, behavior: 'smooth' });
      }
    }, 120);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-800 font-sans selection:bg-[#C5A46E]/30 selection:text-[#0F1E32]">
      
      {/* Dynamic SEO Meta, Open Graph, Canonical & JSON-LD */}
      <HeadSEO activeTab={activeTab as any} selectedApartment={selectedApartment} />

      {/* Dynamic Success Alert Banner */}
      {isSuccessModalOpen && (
        <div id="success-banner" className="fixed top-24 left-1/2 -translate-x-1/2 z-55 bg-emerald-600 text-white py-3 px-6 rounded-xl shadow-xl border border-emerald-500 max-w-md text-sm font-semibold flex items-center gap-2 animate-fade-in">
          <CheckCircle className="w-5 h-5 shrink-0 text-emerald-100" />
          <span>{successMessage}</span>
          <button onClick={() => setIsSuccessModalOpen(false)} className="ml-3 hover:text-amber-200">✕</button>
        </div>
      )}

      {/* Navigation */}
      {activeTab !== 'admin' && (
        <Navbar 
          activeTab={activeTab} 
          setActiveTab={handleTabChange} 
          onOpenConsultation={() => setIsConsultationOpen(true)} 
          activeFilterType={activeFilters.type}
        />
      )}

      {/* RENDER ACTIVE TAB */}
      <main className="flex-grow">
        
        {/* =============== HOME VIEW =============== */}
        {activeTab === 'home' && (
          <div className="animate-fade-in">
            {/* Hero Segment */}
            <div className="relative text-[#0F1E32] pt-40 pb-20 sm:pt-42 md:pt-44 md:pb-28 lg:pt-48 lg:pb-32 px-4 overflow-hidden border-b border-slate-100 shadow-sm bg-slate-900">
              {/* Background photo or video with soft, gentle gradient for text legibility */}
              <div className="absolute inset-0 z-0 bg-slate-900">
                {(() => {
                  const rawMedia = (content.hero.bgImage && content.hero.bgImage !== '/hero.jpg') ? content.hero.bgImage : "/hero.webp";
                  const isVideo = isVideoUrl(rawMedia);
                  const safeSrc = formatMediaUrl(rawMedia);

                  if (isVideo) {
                    return (
                      <video 
                        key={safeSrc}
                        src={safeSrc}
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        preload="auto"
                        className="w-full h-full object-cover object-center"
                      />
                    );
                  }

                  return (
                    <img 
                      src={safeSrc} 
                      alt={content.seo?.pages?.home?.imageAltHero || "Модерен жилищен комплекс във Варна - Варна Имотен Център"} 
                      className="w-full h-full object-cover object-center"
                    />
                  );
                })()}
                {/* Мек воал - осигурява четимост на текста, без да скрива видеото/снимката */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/45 md:via-white/25 to-transparent pointer-events-none"></div>
              </div>

              {/* Foreground content */}
              <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className={`flex flex-col items-start text-left ${apartments.length > 0 ? 'lg:col-span-7' : 'lg:col-span-9'}`}>
                  {/* Category Eyebrow: ИМОТИ ВЪВ ВАРНА • ПРОДАЖБИ • НАЕМИ • КОНСУЛТАЦИИ - Лек тънък черен контур на буквите за контраст */}
                  <span 
                    className="text-[13px] sm:text-[14px] md:text-[15px] font-bold tracking-[0.18em] uppercase mb-3 text-[#8A6324] drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.35)]"
                    style={{ WebkitTextStroke: '0.35px rgba(0, 0, 0, 0.75)', paintOrder: 'stroke fill' }}
                  >
                    {content.hero.badge || 'ИМОТИ ВЪВ ВАРНА • ПРОДАЖБИ • НАЕМИ • КОНСУЛТАЦИИ'}
                  </span>

                  {/* Main Title in bold, prominent Serif Typography */}
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[66px] font-bold tracking-tight leading-[1.1] text-[#0A192F] mt-1 font-heading">
                    {content.seo?.pages?.home?.h1 ? (
                      content.seo.pages.home.h1
                    ) : (
                      <>
                        {content.hero.titleLine1} <br />
                        {content.hero.titleLine2}
                      </>
                    )}
                  </h1>

                  {/* Subtitle - substantially larger, bold and easy to read */}
                  <p className="text-base sm:text-lg md:text-xl text-[#1F2937] max-w-xl mt-5 leading-relaxed font-medium">
                    {content.seo?.pages?.home?.h2 || content.hero.subtitle}
                  </p>
                  
                  {/* Interactive translucent pills: Продажби, Наеми, Подготовка на документи */}
                  <div className="flex items-center gap-3.5 mt-8 flex-wrap">
                    <div 
                      onClick={() => handleTabChange('sales')}
                      className="flex items-center gap-3 bg-white/80 hover:bg-white backdrop-blur-md border border-white/80 rounded-full pl-2 pr-5 py-2 shadow-md text-sm font-bold text-[#0F1E32] cursor-pointer hover:shadow-lg transition-all"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#0F2338] flex items-center justify-center text-[#E5C384] shrink-0 shadow-sm">
                        <Home className="w-4 h-4" />
                      </div>
                      <span className="text-[#0F1E32] tracking-wide">
                        {content.hero.interactiveBadges?.salesBadge || 'Продажби'}
                      </span>
                    </div>

                    <div 
                      onClick={() => handleTabChange('rentals')}
                      className="flex items-center gap-3 bg-white/80 hover:bg-white backdrop-blur-md border border-white/80 rounded-full pl-2 pr-5 py-2 shadow-md text-sm font-bold text-[#0F1E32] cursor-pointer hover:shadow-lg transition-all"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#0F2338] flex items-center justify-center text-[#E5C384] shrink-0 shadow-sm">
                        <Key className="w-4 h-4" />
                      </div>
                      <span className="text-[#0F1E32] tracking-wide">
                        {content.hero.interactiveBadges?.rentalsBadge || 'Наеми'}
                      </span>
                    </div>

                    <div 
                      onClick={() => setIsConsultationOpen(true)}
                      className="flex items-center gap-3 bg-white/80 hover:bg-white backdrop-blur-md border border-white/80 rounded-full pl-2 pr-5 py-2 shadow-md text-sm font-bold text-[#0F1E32] cursor-pointer hover:shadow-lg transition-all"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#0F2338] flex items-center justify-center text-[#E5C384] shrink-0 shadow-sm">
                        <FileText className="w-4 h-4" />
                      </div>
                      <span className="text-[#0F1E32] tracking-wide">
                        {content.hero.interactiveBadges?.documentsBadge || 'Подготовка на документи'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Hero: Featured property card (renders selectable hero property) */}
                {apartments.length > 0 && heroFeaturedProperty && (
                  <div className="lg:col-span-5 hidden lg:block">
                    <div 
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenApartment(heroFeaturedProperty)}
                      className="bg-white rounded-2xl p-4 shadow-2xl border border-white/80 relative animate-fade-in group w-full max-w-[340px] ml-auto cursor-pointer hover:shadow-3xl transition-all select-none"
                    >
                      {/* Apartment Image */}
                      <div className="relative overflow-hidden rounded-xl mb-3.5">
                        <img 
                          src={heroFeaturedProperty.image} 
                          alt={heroFeaturedProperty.title}
                          referrerPolicy="no-referrer"
                          className="w-full h-44 object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=650&q=80';
                          }}
                        />
                      </div>

                      {/* Title & Location */}
                      <h4 className="font-bold text-[17px] text-[#111827] mt-1 line-clamp-1 group-hover:text-[#C5A46E] transition-colors">
                        {typeof heroFeaturedProperty.title === 'object' ? (heroFeaturedProperty.title as any)?.name : heroFeaturedProperty.title}
                      </h4>
                      <div className="text-xs text-slate-500 font-medium mt-0.5">
                        {typeof heroFeaturedProperty.neighborhood === 'object' ? (heroFeaturedProperty.neighborhood as any)?.name : (heroFeaturedProperty.neighborhood || 'Варна')}
                      </div>

                      {/* Price in EUR */}
                      <div className="flex items-baseline mt-2">
                        <span className="text-xl font-extrabold text-[#111827]">€{heroFeaturedProperty.price.toLocaleString('bg-BG')}</span>
                      </div>
                      
                      {/* 3 Features columns: ID, Area, Type */}
                      <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 mt-3 text-center">
                        <div className="flex flex-col items-center">
                          <Layers className="w-4 h-4 text-[#B8924E] mb-1" />
                          <span className="text-[11px] text-slate-600 font-medium">ID: {heroFeaturedProperty.id}</span>
                        </div>
                        <div className="flex flex-col items-center border-x border-slate-100">
                          <Building className="w-4 h-4 text-[#B8924E] mb-1" />
                          <span className="text-[11px] text-slate-600 font-medium">{heroFeaturedProperty.area} m²</span>
                        </div>
                        <div className="flex flex-col items-center">
                          <Home className="w-4 h-4 text-[#B8924E] mb-1" />
                          <span className="text-[11px] text-slate-600 font-medium">
                            {heroFeaturedProperty.rooms ? `${heroFeaturedProperty.rooms}-стаен` : (heroFeaturedProperty.type === 'sale' ? 'Продажба' : 'Наем')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Live Search Console container styled exactly as in user reference */}
            <div className="px-4 -mt-10 relative z-30">
              <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/60 border border-slate-100 p-5 md:p-6 max-w-6xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3.5 items-end">
                  {/* Field 1: Вид оферта */}
                  <div className="lg:col-span-3">
                    <label htmlFor="hero-offer-type" className="block text-[11px] font-medium text-slate-500 mb-1.5 pl-0.5">
                      Вид оферта
                    </label>
                    <div className="relative">
                      <select 
                        id="hero-offer-type"
                        aria-label="Изберете вид оферта"
                        value={activeFilters.type || 'all'}
                        onChange={(e) => {
                          const newType = e.target.value as any;
                          setActiveFilters(prev => ({ 
                            ...prev, 
                            type: newType,
                            priceMin: (prev.type === 'rent' && newType === 'sale') || (prev.type === 'sale' && newType === 'rent') ? '' : prev.priceMin,
                            priceMax: (prev.type === 'rent' && newType === 'sale') || (prev.type === 'sale' && newType === 'rent') ? '' : prev.priceMax
                          }));
                        }}
                        className="w-full bg-slate-50/80 hover:bg-slate-50 border border-slate-200/90 rounded-xl px-3.5 py-2.5 text-sm text-[#0F1E32] font-medium focus:outline-none focus:ring-2 focus:ring-[#007BB5]/30 focus:border-[#007BB5] transition-all appearance-none cursor-pointer"
                      >
                        <option value="all">Всички оферти</option>
                        <option value="sale">Продажби</option>
                        <option value="rent">Под наем</option>
                      </select>
                      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  {/* Field 2: Град */}
                  <div className="lg:col-span-2">
                    <label htmlFor="hero-city" className="block text-[11px] font-medium text-slate-500 mb-1.5 pl-0.5">
                      Град
                    </label>
                    <div className="relative">
                      <select 
                        id="hero-city"
                        aria-label="Изберете град"
                        value={activeFilters.city || 'Варна'}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full bg-slate-50/80 hover:bg-slate-50 border border-slate-200/90 rounded-xl px-3.5 py-2.5 text-sm text-[#0F1E32] font-medium focus:outline-none focus:ring-2 focus:ring-[#007BB5]/30 focus:border-[#007BB5] transition-all appearance-none cursor-pointer"
                      >
                        <option value="Варна">Варна</option>
                        <option value="">Всички</option>
                      </select>
                      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  {/* Field 3: Квартал */}
                  <div className="lg:col-span-3">
                    <label htmlFor="hero-neighborhood" className="block text-[11px] font-medium text-slate-500 mb-1.5 pl-0.5">
                      Квартал
                    </label>
                    <div className="relative">
                      <select 
                        id="hero-neighborhood"
                        aria-label="Изберете квартал"
                        value={activeFilters.neighborhood || ''}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, neighborhood: e.target.value }))}
                        className="w-full bg-slate-50/80 hover:bg-slate-50 border border-slate-200/90 rounded-xl px-3.5 py-2.5 text-sm text-[#0F1E32] font-medium focus:outline-none focus:ring-2 focus:ring-[#007BB5]/30 focus:border-[#007BB5] transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Избери квартал</option>
                        {uniqueNeighborhoods.map((n, i) => (
                          <option key={i} value={n}>{n}</option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  {/* Field 4: Вид имот */}
                  <div className="lg:col-span-3">
                    <label htmlFor="hero-property-type" className="block text-[11px] font-medium text-slate-500 mb-1.5 pl-0.5">
                      Вид имот
                    </label>
                    <div className="relative">
                      <select 
                        id="hero-property-type"
                        aria-label="Изберете вид имот"
                        value={activeFilters.propertyType || ''}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, propertyType: e.target.value }))}
                        className="w-full bg-slate-50/80 hover:bg-slate-50 border border-slate-200/90 rounded-xl px-3.5 py-2.5 text-sm text-[#0F1E32] font-medium focus:outline-none focus:ring-2 focus:ring-[#007BB5]/30 focus:border-[#007BB5] transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Избери вид имот</option>
                        {uniquePropertyTypes.map((t: any, i) => {
                          const val = typeof t === 'object' ? t?.name || t?.label || '' : String(t || '');
                          if (!val) return null;
                          return <option key={i} value={val}>{val}</option>;
                        })}
                      </select>
                      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  {/* Search Button (Accessible Blue #007BB5 button with Search Icon) */}
                  <div className="lg:col-span-1">
                    <button 
                      id="hero-search-submit"
                      type="button"
                      aria-label="Търси имоти по избраните критерии"
                      onClick={handleHeroSearch}
                      className="w-full bg-[#007BB5] hover:bg-[#00699B] text-white py-2.5 px-4 rounded-xl font-medium text-sm flex items-center justify-center gap-1.5 shadow-sm hover:shadow transition-all active:scale-95 cursor-pointer h-[42px]"
                    >
                      <Search className="w-4 h-4 stroke-[2.5]" />
                      <span className="font-semibold text-sm">Търси</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick trust badges matching screenshot: Актуални оферти • Коректно обслужване • Лично отношение */}
            <div className="max-w-4xl mx-auto px-4 py-8 flex flex-wrap items-center justify-center gap-5 sm:gap-7 text-xs md:text-sm font-medium text-[#4B5563] mt-2">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span>{content.hero.trustBadges?.badge1 || 'Актуални оферти'}</span>
              </div>
              <span className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden sm:inline-block"></span>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span>{content.hero.trustBadges?.badge2 || 'Коректно обслужване'}</span>
              </div>
              <span className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden sm:inline-block"></span>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span>{content.hero.trustBadges?.badge3 || 'Лично отношение'}</span>
              </div>
            </div>

            {/* "ПОДБРАНИ ОФЕРТИ" (Featured properties or Live search results) Grid section */}
            <div id="featured-properties-heading" className="max-w-7xl mx-auto px-4 py-16 scroll-mt-24">
              <div className="flex justify-between items-end gap-4 mb-10 pb-4 border-b border-gray-100 flex-wrap">
                <div>
                  <h2 className="text-2xl md:text-3xl font-black text-[#0F1E32] tracking-tight uppercase">
                    {hasActiveHomeFilters ? 'Резултати от търсенето' : content.featuredSection.title}
                  </h2>
                  <p className="text-xs text-slate-600 mt-1">
                    {hasActiveHomeFilters 
                      ? `Намерени са ${homeFilteredProperties.length} оферти по избраните критерии` 
                      : content.featuredSection.subtitle}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {hasActiveHomeFilters && (
                    <button 
                      type="button"
                      onClick={() => setActiveFilters({
                        type: activeFilters.type === 'rent' ? 'rent' : (activeFilters.type === 'sale' ? 'sale' : 'all'),
                        neighborhood: '',
                        city: 'Варна',
                        propertyType: '',
                        priceMin: '',
                        priceMax: '',
                        areaMin: '',
                        areaMax: '',
                        rooms: '',
                        searchWord: '',
                        sortBy: 'newest'
                      })}
                      className="text-xs font-bold text-red-600 hover:text-red-700 transition px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 cursor-pointer"
                    >
                      Изчисти филтрите
                    </button>
                  )}
                  <button 
                    id="view-all-sales"
                    type="button"
                    onClick={handleHeroSearch}
                    className="text-xs font-bold text-[#916726] hover:text-[#0F1E32] transition flex items-center gap-1 shrink-0 cursor-pointer"
                  >
                    {hasActiveHomeFilters ? 'Към пълния каталог →' : content.featuredSection.viewAllButtonText}
                  </button>
                </div>
              </div>

              {/* Grid block */}
              {homeFilteredProperties.length === 0 ? (
                <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 mx-auto flex items-center justify-center mb-3">
                    <Home className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-base text-[#0F1E32]">
                    {hasActiveHomeFilters ? 'Няма намерени оферти по избраните критерии' : 'В момента няма заредени оферти'}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    {hasActiveHomeFilters 
                      ? 'Опитайте да изберете друг квартал или изчистете филтрите, за да разгледате всички актуални обяви.' 
                      : 'Офертите се обновяват. Моля, проверете отново след малко или се свържете с нас за съдействие.'}
                  </p>
                  {hasActiveHomeFilters && (
                    <button 
                      type="button"
                      onClick={() => setActiveFilters({
                        type: 'all',
                        neighborhood: '',
                        city: 'Варна',
                        propertyType: '',
                        priceMin: '',
                        priceMax: '',
                        areaMin: '',
                        areaMax: '',
                        rooms: '',
                        searchWord: '',
                        sortBy: 'newest'
                      })}
                      className="mt-4 px-4 py-2 bg-[#0F1E32] text-white text-xs font-bold uppercase rounded-lg hover:bg-slate-800 transition cursor-pointer"
                    >
                      Покажи всички оферти
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {homeFilteredProperties.slice(0, 8).map((apt) => (
                    <PropertyCard 
                      key={apt.id}
                      apartment={apt}
                      isFavorite={favorites.includes(apt.id)}
                      onToggleFavorite={handleToggleFavorite}
                      onSelect={handleOpenApartment}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* "НАШИТЕ УСЛУГИ" (Our Services) Column track layout */}
            <div className="bg-[#0F1E32] text-white py-16 md:py-20 px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center max-w-2xl mx-auto mb-16">
                  <span className="text-xs text-[#C5A46E] font-bold tracking-widest uppercase">{content.servicesSection.badge}</span>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-1">{content.servicesSection.title}</h2>
                  <p className="text-sm text-slate-300 mt-2 font-light">{content.servicesSection.subtitle}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {(content.servicesSection.teaserCards || []).map((card, idx) => (
                    <div key={card.id || idx} className="bg-[#15253A] rounded-2xl p-8 border border-slate-800 flex flex-col items-start hover:-translate-y-1 transition duration-300">
                      <div className="bg-[#C5A46E]/10 p-3 rounded-xl mb-6">
                        {idx === 0 && <Handshake className="w-8 h-8 text-[#C5A46E]" />}
                        {idx === 1 && <FileText className="w-8 h-8 text-[#C5A46E]" />}
                        {idx >= 2 && <Compass className="w-8 h-8 text-[#C5A46E]" />}
                      </div>
                      <h3 className="font-bold text-xl text-white mb-3 uppercase">{card.title}</h3>
                      <p className="text-sm text-slate-300 leading-relaxed font-light mb-4 text-left">
                        {card.description}
                      </p>
                      <button onClick={() => setActiveTab('services')} className="text-xs font-bold text-[#C5A46E] hover:text-white transition mt-auto flex items-center gap-1.5 align-middle">
                        {card.buttonText || 'Виж подробности →'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* "НАШАТА ИСТОРИЯ И МИСИЯ" Read More section banner */}
            <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative rounded-2xl overflow-hidden h-96 shadow-xl border border-gray-100">
                <img 
                  src={content.aboutSection.image || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"} 
                  alt={content.seo?.pages?.home?.imageAltSecondary || "Интериор на луксозен апартамент във Варна"} 
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-col items-start text-left">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">
                  {content.aboutSection.badge || 'ЗА АГЕНЦИЯТА'}
                </span>
                <h2 className="text-2xl md:text-3xl font-black text-[#0F1E32] tracking-tight mt-1 mb-4 uppercase">
                  {content.aboutSection.title}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed font-light mb-4">
                  {content.aboutSection.paragraph1}
                </p>
                <p className="text-sm text-slate-600 leading-relaxed font-light mb-6">
                  {content.aboutSection.paragraph2}
                </p>
                <button 
                  id="btn-about-more"
                  onClick={() => handleTabChange('about')}
                  className="px-6 py-3 bg-[#966D29] text-white hover:bg-[#835D20] font-bold text-xs uppercase tracking-widest rounded-lg transition shadow-sm cursor-pointer"
                >
                  {content.aboutSection.buttonText || 'Научете повече за нас →'}
                </button>
              </div>
            </div>

            {/* Google Reviews Screenshot Carousel on Home */}
            <div className="max-w-7xl mx-auto px-4 -mt-4 mb-16">
              <GoogleReviewsCarousel 
                screenshots={content.aboutSection.googleReviewsScreenshots}
                title={content.aboutSection.reviewsTitle}
                subtitle={content.aboutSection.reviewsSubtitle}
                googleReviewsUrl={content.aboutSection.googleReviewsUrl}
                rating={content.aboutSection.reviewsRating}
                ratingText={content.aboutSection.reviewsRatingText}
                isAdmin={user !== null}
                onOpenAdminReviews={() => {
                  setActiveTab('admin');
                }}
              />
            </div>



            {/* Popular Regions category selector on Home page */}
            <div className="max-w-7xl mx-auto px-4 -mt-4 mb-16">
              {renderPopularRegionsSection()}
            </div>

            {/* General Lead Form Contact banner */}
            <div className="bg-[#0F1E32] text-white py-16 px-4 relative overflow-hidden">
              <div className="absolute inset-0 z-0 opacity-15">
                <img 
                  src={content.ctaBanner.bgImage || "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1000&q=80"} 
                  alt="City background" 
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="relative z-10 max-w-4xl mx-auto text-center">
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight leading-snug uppercase">
                  {content.ctaBanner.titlePrefix}<span className="text-[#C5A46E]">{content.ctaBanner.highlightedText}</span>{content.ctaBanner.titleSuffix}
                </h3>
                <p className="text-sm text-slate-300 mt-2 max-w-xl mx-auto font-light">
                  {content.ctaBanner.subtitle}
                </p>
                <div className="mt-8 flex gap-4.5 justify-center flex-wrap">
                  <a 
                    href={`tel:${(content.contactsPage?.phone1 || AGENCY_CONFIG.phone).replace(/[^\d+]/g, '')}`}
                    className="px-6 py-3 bg-[#966D29] hover:bg-[#835D20] text-white font-bold text-xs uppercase tracking-widest rounded-lg transition shadow-md flex items-center gap-1.5"
                  >
                    <Phone className="w-4 h-4" />
                    {content.ctaBanner.phoneButtonText || '+359 878 859 385'}
                  </a>
                  <button 
                    id="btn-home-contact"
                    onClick={() => setActiveTab('contacts')}
                    className="px-6 py-3 bg-white hover:bg-slate-100 text-[#0F1E32] font-bold text-xs uppercase tracking-widest rounded-lg transition shadow-md"
                  >
                    {content.ctaBanner.contactButtonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* =============== PROPERTIES FOR SALE / ALL OFFERS VIEW =============== */}
        {(activeTab === 'sales' || activeTab === 'all') && (
          <div className="animate-fade-in font-sans pb-16">
            <div className="relative bg-[#F4F7FA] text-[#0F1E32] pt-38 pb-16 md:pt-44 md:pb-20 text-center px-4 overflow-hidden border-b border-slate-100 shadow-sm">
              {/* Background photo overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={content.salesPage.bgImage || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&auto=format&fit=crop&q=80"} 
                  alt={content.seo?.pages?.sales?.imageAltHero || "Имоти за продажба във Варна - апартаменти и къщи"} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-[#F4F7FA]/90 backdrop-blur-[1px]"></div>
              </div>
              <div className="relative z-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">
                  {activeFilters.type === 'all' || activeTab === 'all' ? 'ПЪЛЕН КАТАЛОГ ОФЕРТИ' : content.salesPage.badge}
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mt-1 uppercase tracking-tight text-[#0F1E32]">
                  {activeFilters.type === 'all' || activeTab === 'all' ? 'Всички оферти във Варна' : (content.seo?.pages?.sales?.h1 || content.salesPage.title)}
                </h1>
                <h2 className="text-sm text-slate-600 mt-2 max-w-lg mx-auto font-medium">
                  {activeFilters.type === 'all' || activeTab === 'all' ? 'Разгледайте всички актуални оферти за продажба и под наем' : (content.seo?.pages?.sales?.h2 || content.salesPage.subtitle)}
                </h2>
                {content.seo?.pages?.sales?.pageDescription && content.seo.pages.sales.pageDescription !== (content.seo?.pages?.sales?.h2 || content.salesPage.subtitle) && (
                  <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto font-light leading-relaxed">
                    {content.seo.pages.sales.pageDescription}
                  </p>
                )}
              </div>
            </div>

            {/* Filter Search Core */}
            <div id="search-filters-container" className="px-4">
              <SearchFilters 
                initialType={activeTab === 'all' ? 'all' : (activeFilters.type || 'sale')} 
                currentFilters={activeFilters}
                onFilterChange={handleFilterChange} 
                neighborhoods={uniqueNeighborhoods} 
                propertyTypes={uniquePropertyTypes} 
              />
            </div>

            {/* Grid listings segment */}
            <div id="catalog-results-anchor" className="max-w-7xl mx-auto px-4 mt-12 scroll-mt-24">
              <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-100">
                <span className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Намерени: <span className="text-[#916726] text-sm font-extrabold">
                    {filteredApartments.length} {activeFilters.type === 'all' ? 'оферти (продажби и наеми)' : 'оферти за продажба'}
                  </span>
                </span>
                {activeFilters.neighborhood && (
                  <span className="text-xs bg-[#C5A46E]/15 text-[#8C6221] px-2.5 py-1 rounded font-bold uppercase">
                    Филтриран район: {activeFilters.neighborhood}
                  </span>
                )}
              </div>

              {filteredApartments.length === 0 ? (
                <div id="no-sales-results" className="text-center py-20 bg-white rounded-2xl shadow border border-slate-100">
                  <div className="text-slate-300 text-5xl mb-4">🏠</div>
                  <h3 className="font-bold text-xl text-[#0F1E32]">Няма намерени оферти</h3>
                  <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">Не открихме съвпадение за зададените критерии на филтъра. Моля, нулирайте филтрите или проверете други квартали.</p>
                  <button 
                    id="reset-sales-filter"
                    onClick={() => setActiveFilters({
                      type: 'sale',
                      neighborhood: '',
                      propertyType: '',
                      priceMin: '',
                      priceMax: '',
                      areaMin: '',
                      areaMax: '',
                      rooms: '',
                      searchWord: '',
                      sortBy: 'newest'
                    })}
                    className="mt-6 px-5 py-2.5 bg-[#C5A46E] text-white text-xs font-bold uppercase tracking-widest rounded-lg shadow hover:bg-amber-600 transition"
                  >
                    Изчисти филтрите
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {paginatedSalesApartments.map((apt) => (
                      <PropertyCard 
                        key={apt.id}
                        apartment={apt}
                        isFavorite={favorites.includes(apt.id)}
                        onToggleFavorite={handleToggleFavorite}
                        onSelect={handleOpenApartment}
                      />
                    ))}
                  </div>
                  {renderPagination(salesPage, totalSalesPages, setSalesPage)}
                </>
              )}
            </div>

            {/* Popular Regions category selector */}
            <div className="px-4">
              {renderPopularRegionsSection()}
            </div>
          </div>
        )}


        {/* =============== PROPERTIES FOR RENT VIEW =============== */}
        {activeTab === 'rentals' && (
          <div className="animate-fade-in font-sans pb-16">
            <div className="relative bg-[#F4F7FA] text-[#0F1E32] pt-38 pb-16 md:pt-44 md:pb-20 text-center px-4 overflow-hidden border-b border-slate-100 shadow-sm">
              {/* Background photo overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={content.rentalsPage.bgImage || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&auto=format&fit=crop&q=80"} 
                  alt={content.seo?.pages?.rentals?.imageAltHero || "Имоти под наем Варна - дългосрочен наем на апартаменти"} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-[#F4F7FA]/90 backdrop-blur-[1px]"></div>
              </div>
              <div className="relative z-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.rentalsPage.badge}</span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mt-1 uppercase tracking-tight text-[#0F1E32]">
                  {content.seo?.pages?.rentals?.h1 || content.rentalsPage.title}
                </h1>
                <h2 className="text-sm text-slate-600 mt-2 max-w-lg mx-auto font-medium">
                  {content.seo?.pages?.rentals?.h2 || content.rentalsPage.subtitle}
                </h2>
                {content.seo?.pages?.rentals?.pageDescription && content.seo.pages.rentals.pageDescription !== (content.seo?.pages?.rentals?.h2 || content.rentalsPage.subtitle) && (
                  <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto font-light leading-relaxed">
                    {content.seo.pages.rentals.pageDescription}
                  </p>
                )}
              </div>
            </div>

            {/* Filter Search Core */}
            <div id="search-filters-container-rentals" className="px-4">
              <SearchFilters 
                initialType="rent" 
                currentFilters={activeFilters}
                onFilterChange={handleFilterChange} 
                neighborhoods={uniqueNeighborhoods} 
                propertyTypes={uniquePropertyTypes} 
              />
            </div>

            {/* Rental list */}
            <div id="catalog-results-anchor" className="max-w-7xl mx-auto px-4 mt-12 scroll-mt-24">
              <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-100">
                <span className="text-xs font-bold text-[#0F1E32] uppercase tracking-wider">
                  Намерени: <span className="text-[#1D608A] text-sm font-extrabold">
                    {filteredApartments.length} {activeFilters.type === 'all' ? 'оферти (продажби и наеми)' : 'оферти под наем'}
                  </span>
                </span>
              </div>

              {filteredApartments.length === 0 ? (
                <div id="no-rentals-results" className="text-center py-20 bg-white rounded-2xl shadow border border-slate-100">
                  <div className="text-slate-300 text-5xl mb-4">🏠</div>
                  <h3 className="font-bold text-xl text-[#0F1E32]">Няма намерени свободни оферти под наем</h3>
                  <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">Не успяхме да открием съответствия на Вашето търсене. Моля нулирайте филтрите или изберете друг разширен бюджет.</p>
                  <button 
                    id="reset-rentals-filter"
                    onClick={() => setActiveFilters({
                      type: 'rent',
                      neighborhood: '',
                      propertyType: '',
                      priceMin: '',
                      priceMax: '',
                      areaMin: '',
                      areaMax: '',
                      rooms: '',
                      searchWord: '',
                      sortBy: 'newest'
                    })}
                    className="mt-6 px-5 py-2.5 bg-[#1D608A] text-white text-xs font-bold uppercase tracking-widest rounded-lg shadow hover:bg-[#1a557b] transition"
                  >
                    Изчисти филтрите
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {paginatedRentalsApartments.map((apt) => (
                      <PropertyCard 
                        key={apt.id}
                        apartment={apt}
                        isFavorite={favorites.includes(apt.id)}
                        onToggleFavorite={handleToggleFavorite}
                        onSelect={handleOpenApartment}
                      />
                    ))}
                  </div>
                  {renderPagination(rentalsPage, totalRentalsPages, setRentalsPage)}
                </>
              )}
            </div>

            {/* "КАК РАБОТИМ ПРИ НАЕМАHE" Row section - Page 5 */}
            <div className="max-w-7xl mx-auto px-4 py-16 mt-16 border-t border-gray-100">
              <div className="text-center max-w-2xl mx-auto mb-12">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.rentalsPage.howWeWorkBadge}</span>
                <h3 className="text-2xl md:text-3xl font-black text-[#0F1E32] mt-1 uppercase">{content.rentalsPage.howWeWorkTitle}</h3>
                <p className="text-xs text-slate-600 mt-1.5">{content.rentalsPage.howWeWorkSubtitle}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                {(content.rentalsPage.steps || []).map((step, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-150 shadow-sm relative pt-10">
                    <span className="absolute top-4 left-6 text-2xl font-black text-[#C5A46E]/30 font-sans">{step.number}</span>
                    <h4 className="font-bold text-[#0F1E32] text-sm mb-2 uppercase">{step.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed font-light">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Popular Regions category selector in rentals */}
            <div className="px-4">
              {renderPopularRegionsSection()}
            </div>

            {/* Special tenant form banner */}
            <div className="bg-slate-100 py-16 px-4">
              <div className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-150 text-center">
                <span className="text-[#C5A46E] text-xs font-bold tracking-widest uppercase mb-2 block">{content.rentalsPage.requestBanner?.badge || 'НЕ ОТКРИВАТЕ ОФЕРТА?'}</span>
                <h3 className="text-xl md:text-2xl font-extrabold text-[#0F1E32] uppercase tracking-tight">{content.rentalsPage.requestBanner?.title || 'Изпратете ни писмено запитване'}</h3>
                <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto">{content.rentalsPage.requestBanner?.subtitle || 'Споделете какво търсите (квартал, стаи, цена). Нашите брокери ще намерят имоти извън пазара за Вас!'}</p>
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    triggerSuccess('Запитването е регистрирано. Нашите наемни брокери веднага ще подготвят подходящи оферти за Вас!');
                  }}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 max-w-2xl mx-auto"
                >
                  <input required placeholder="Вашето Име" type="text" className="bg-slate-50 border border-gray-200 text-xs px-3.5 py-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500" />
                  <input required placeholder="Телефон *" type="tel" className="bg-slate-50 border border-gray-200 text-xs px-3.5 py-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500" />
                  <button type="submit" className="bg-[#0F1E32] hover:bg-[#C5A46E] text-white text-xs font-bold tracking-wide uppercase rounded-lg transition py-3">
                    {content.rentalsPage.requestBanner?.buttonText || 'ИЗПРАТИ ЗАПИТВАНЕ 📨'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}


        {/* =============== THE TEAM VIEW =============== */}
        {activeTab === 'team' && (
          <div className="animate-fade-in font-sans pb-16">
            <div className="relative bg-[#F4F7FA] text-[#0F1E32] pt-38 pb-16 md:pt-44 md:pb-20 text-center px-4 overflow-hidden border-b border-slate-100 shadow-sm">
              {/* Background photo overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={content.teamPage.bgImage || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&auto=format&fit=crop&q=80"} 
                  alt={content.seo?.pages?.team?.imageAltHero || "Екип от професионални лицензирани брокери във Варна"} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-[#F4F7FA]/90 backdrop-blur-[1px]"></div>
              </div>
              <div className="relative z-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.teamPage.badge}</span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mt-1 uppercase tracking-tight text-[#0F1E32]">
                  {content.seo?.pages?.team?.h1 || content.teamPage.title}
                </h1>
                <h2 className="text-sm text-slate-600 mt-2 max-w-lg mx-auto font-medium">
                  {content.seo?.pages?.team?.h2 || content.teamPage.subtitle}
                </h2>
                {content.seo?.pages?.team?.pageDescription && content.seo.pages.team.pageDescription !== (content.seo?.pages?.team?.h2 || content.teamPage.subtitle) && (
                  <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto font-light leading-relaxed">
                    {content.seo.pages.team.pageDescription}
                  </p>
                )}
              </div>
            </div>

            {/* Sorter Category Tabs for Team */}
            <div className="max-w-7xl mx-auto px-4 mt-12 mb-10 text-center">
              <div className="inline-flex bg-white p-1 rounded-xl shadow-sm border border-gray-150 flex-wrap justify-center gap-1">
                {[
                  { id: 'all', label: 'Всички' },
                  { id: 'sales', label: 'Продажби' },
                  { id: 'rentals', label: 'Наеми' },
                  { id: 'office', label: 'Офис / Администрация' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    id={`team-filter-${cat.id}`}
                    onClick={() => setTeamCategory(cat.id as any)}
                    className={`px-5 py-2 text-xs font-bold tracking-wider rounded-lg uppercase cursor-pointer transition ${
                      teamCategory === cat.id 
                        ? 'bg-[#0F1E32] text-white' 
                        : 'text-gray-600 hover:text-amber-500 hover:bg-slate-50'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* List of Agents */}
            <div className="max-w-7xl mx-auto px-4">
              {(() => {
                const orderedTeam = agents
                  .slice()
                  .sort((a, b) => (Number(a.order ?? 999999) - Number(b.order ?? 999999)))
                  .filter(a => teamCategory === 'all' || a.category === teamCategory);

                if (orderedTeam.length === 0) {
                  return (
                    <div className="bg-white rounded-2xl p-12 border border-slate-150 text-center max-w-lg mx-auto shadow-sm">
                      <div className="w-12 h-12 rounded-full bg-slate-50 text-[#C5A46E] flex items-center justify-center mx-auto mb-3">
                        <Users className="w-6 h-6" />
                      </div>
                      <h3 className="font-bold text-lg text-[#0F1E32]">Варна Имотен Център</h3>
                      <p className="text-xs text-slate-500 mt-2 mb-5">
                        Свържете се с централния ни офис на посочения телефон или имейл за персонална връзка с брокер за Вашия район.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <a href={`tel:${AGENCY_CONFIG.phone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#0F1E32] text-white hover:bg-[#C5A46E] rounded-lg text-xs font-bold transition">
                          <Phone className="w-3.5 h-3.5 text-[#C5A46E]" /> {AGENCY_CONFIG.phone}
                        </a>
                        <a href={`mailto:${AGENCY_CONFIG.email}`} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-100 text-[#0F1E32] hover:bg-slate-200 rounded-lg text-xs font-bold transition">
                          <Mail className="w-3.5 h-3.5 text-[#C5A46E]" /> {AGENCY_CONFIG.email}
                        </a>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-6">
                    {orderedTeam.map((agent) => (
                    <div key={agent.id} className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-150 overflow-hidden flex flex-col group h-full hover:shadow-lg transition-all">
                      <div className="h-44 sm:h-56 md:h-64 overflow-hidden relative bg-slate-100 shrink-0 flex items-center justify-center">
                        {/* Ambient soft blurred backdrop when image is zoomed out */}
                        <img 
                          src={agent.image} 
                          alt="" 
                          aria-hidden="true"
                          className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-35 pointer-events-none"
                        />
                        <img 
                          src={agent.image} 
                          alt={agent.name} 
                          referrerPolicy="no-referrer"
                          className="w-full h-full relative z-10"
                          style={getPositionedImageStyle(
                            agent.imageZoom,
                            agent.imagePanX,
                            agent.imagePanY,
                            agent.imageFit
                          )}
                        />
                      </div>
                      <div className="p-3 sm:p-5 flex flex-col flex-grow text-center">
                        <h3 className="font-bold text-sm sm:text-base md:text-lg text-[#0F1E32] line-clamp-1">{agent.name}</h3>
                        <p className="text-[10px] sm:text-xs text-[#C5A46E] font-semibold uppercase mt-0.5 line-clamp-1">{agent.role}</p>
                        
                        <div className="mt-2.5 sm:mt-4 pt-2.5 sm:pt-4 border-t border-gray-100 flex flex-col gap-1.5 sm:gap-2 mt-auto">
                          <a 
                            href={`tel:${agent.phone.replace(/[^\d+]/g, '')}`} 
                            className="flex items-center justify-center gap-1.5 py-1.5 sm:py-2 px-2 sm:px-3 bg-slate-50 hover:bg-[#0F1E32] hover:text-white rounded-lg text-[11px] sm:text-xs font-bold text-[#0F1E32] border border-gray-150 transition"
                          >
                            <Phone className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-[#C5A46E] shrink-0" />
                            <span className="truncate">{agent.phone}</span>
                          </a>
                          {agent.email && (
                            <span className="text-[9px] sm:text-[10px] text-gray-400 font-mono italic truncate" title={agent.email}>
                              {agent.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}


        {/* =============== OUR SERVICES VIEW =============== */}
        {activeTab === 'services' && (
          <div className="animate-fade-in font-sans pb-16">
            <div className="relative bg-[#F4F7FA] text-[#0F1E32] pt-38 pb-16 md:pt-44 md:pb-20 text-center px-4 overflow-hidden border-b border-slate-100 shadow-sm">
              {/* Background photo overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={content.servicesPage.bgImage || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&auto=format&fit=crop&q=80"} 
                  alt={content.seo?.pages?.services?.imageAltHero || "Брокерски и правни услуги за недвижими имоти Варна"} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-[#F4F7FA]/90 backdrop-blur-[1px]"></div>
              </div>
              <div className="relative z-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.servicesPage.badge}</span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mt-1 uppercase tracking-tight text-[#0F1E32]">
                  {content.seo?.pages?.services?.h1 || content.servicesPage.title}
                </h1>
                <h2 className="text-sm text-slate-600 mt-2 max-w-lg mx-auto font-medium">
                  {content.seo?.pages?.services?.h2 || content.servicesPage.subtitle}
                </h2>
                {content.seo?.pages?.services?.pageDescription && content.seo.pages.services.pageDescription !== (content.seo?.pages?.services?.h2 || content.servicesPage.subtitle) && (
                  <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto font-light leading-relaxed">
                    {content.seo.pages.services.pageDescription}
                  </p>
                )}
              </div>
            </div>

            {/* List of services with details dropdowns */}
            <div className="max-w-4xl mx-auto px-4 mt-12 grid grid-cols-1 gap-6">
              {((content.servicesPage?.servicesList && content.servicesPage.servicesList.length > 0) ? content.servicesPage.servicesList : INITIAL_SERVICES).map((serv) => (
                <div key={serv.id} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-150 flex flex-col md:flex-row gap-6 items-start">
                  <div className="bg-amber-50 p-4 rounded-xl shrink-0 text-[#916726]">
                    {serv.icon === 'Handshake' && <Handshake className="w-8 h-8" />}
                    {serv.icon === 'FileText' && <FileText className="w-8 h-8" />}
                    {serv.icon === 'TrendingUp' && <Layers className="w-8 h-8" />}
                    {serv.icon === 'Heart' && <Heart className="w-8 h-8" />}
                  </div>

                  <div className="text-left w-full">
                    <h3 className="font-extrabold text-[#0F1E32] text-lg uppercase tracking-tight">{serv.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed font-light">{serv.description}</p>
                    
                    {/* Collapsible checklist items */}
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 pt-4 border-t border-gray-100 text-xs text-[#0F1E32]/90">
                      {serv.details.map((det, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 leading-tight">
                          <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{det}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ Accordion Section - Page 8 */}
            <div className="max-w-3xl mx-auto px-4 mt-20">
              <div className="text-center mb-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.servicesPage.faqBadge}</span>
                <h3 className="text-2xl mt-1 uppercase font-black text-[#0F1E32]">{content.servicesPage.faqTitle}</h3>
                <p className="text-xs text-slate-600 mt-1">{content.servicesPage.faqSubtitle}</p>
              </div>

              {/* Accordion List */}
              <div className="space-y-3.5">
                {INITIAL_FAQS.map((faq, idx) => {
                  const isOpened = activeFaqIdx === idx;
                  return (
                    <div 
                      key={idx} 
                      className="bg-white border border-gray-150 rounded-xl overflow-hidden shadow-sm hover:border-gray-300 transition-colors"
                    >
                      <button
                        id={`faq-btn-${idx}`}
                        onClick={() => setActiveFaqIdx(isOpened ? null : idx)}
                        className="w-full text-left py-4 px-5 font-bold text-sm text-[#0F1E32] flex justify-between items-center focus:outline-none"
                      >
                        <span>{faq.question}</span>
                        {isOpened ? (
                          <ChevronUp className="w-4 h-4 text-[#916726]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#916726]" />
                        )}
                      </button>
                      
                      {isOpened && (
                        <div id={`faq-answer-${idx}`} className="px-5 pb-5 pt-1 text-xs text-slate-600 leading-relaxed text-left border-t border-gray-50 bg-slate-50/50">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}


        {/* =============== CONTACTS VIEW =============== */}
        {activeTab === 'contacts' && (
          <div className="animate-fade-in font-sans pb-16">
            <div className="relative bg-[#F4F7FA] text-[#0F1E32] pt-38 pb-16 md:pt-44 md:pb-20 text-center px-4 overflow-hidden border-b border-slate-100 shadow-sm">
              {/* Background photo overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={content.contactsPage.bgImage || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&auto=format&fit=crop&q=80"} 
                  alt={content.seo?.pages?.contacts?.imageAltHero || "Централен офис на Варна Имотен Център"} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-[#F4F7FA]/90 backdrop-blur-[1px]"></div>
              </div>
              <div className="relative z-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.contactsPage.badge}</span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mt-1 uppercase tracking-tight text-[#0F1E32]">
                  {content.seo?.pages?.contacts?.h1 || content.contactsPage.title}
                </h1>
                <h2 className="text-sm text-slate-600 mt-2 max-w-lg mx-auto font-medium">
                  {content.seo?.pages?.contacts?.h2 || content.contactsPage.subtitle}
                </h2>
                {content.seo?.pages?.contacts?.pageDescription && content.seo.pages.contacts.pageDescription !== (content.seo?.pages?.contacts?.h2 || content.contactsPage.subtitle) && (
                  <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto font-light leading-relaxed">
                    {content.seo.pages.contacts.pageDescription}
                  </p>
                )}
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Side: Contact details */}
              <div className="lg:col-span-4 space-y-5 text-left">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-150">
                  <h3 className="font-extrabold text-[#0F1E32] text-lg uppercase tracking-tight mb-5 pb-2 border-b border-gray-100">{content.contactsPage.companyName || 'Имотен Център Варна'}</h3>
                  
                  <div className="space-y-4 text-xs text-slate-700 font-sans">
                    <div className="flex gap-3">
                      <MapPin className="w-5 h-5 text-[#C5A46E] shrink-0" />
                      <div>
                        <p className="font-bold">Адрес на офиса</p>
                        <p className="text-slate-500 mt-0.5">{content.contactsPage.address || AGENCY_CONFIG.address}</p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Phone className="w-5 h-5 text-[#C5A46E] shrink-0" />
                      <div>
                        <p className="font-bold">Телефонни линии</p>
                        <a href={`tel:${(content.contactsPage.phone1 || AGENCY_CONFIG.phone).replace(/[^\d+]/g, '')}`} className="text-slate-500 hover:text-[#C5A46E] block mt-0.5">{content.contactsPage.phone1 || AGENCY_CONFIG.phone}</a>
                        {content.contactsPage.phone2 && (
                          <a href={`tel:${content.contactsPage.phone2.replace(/[^\d+]/g, '')}`} className="text-slate-500 hover:text-[#C5A46E] block">{content.contactsPage.phone2}</a>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Mail className="w-5 h-5 text-[#C5A46E] shrink-0" />
                      <div>
                        <p className="font-bold">Имейл за запитвания</p>
                        <a href={`mailto:${content.contactsPage.email || AGENCY_CONFIG.email}`} className="text-slate-500 hover:text-[#C5A46E] block mt-0.5">{content.contactsPage.email || AGENCY_CONFIG.email}</a>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Clock className="w-5 h-5 text-[#C5A46E] shrink-0" />
                      <div>
                        <p className="font-bold">Работно време</p>
                        <p className="text-slate-500 mt-0.5">{content.contactsPage.workingHoursWeekdays || AGENCY_CONFIG.workingHours.weekdays}</p>
                        <p className="text-slate-500">{content.contactsPage.workingHoursSaturday || AGENCY_CONFIG.workingHours.saturday}</p>
                        <p className="text-slate-500 font-semibold text-red-500">{content.contactsPage.workingHoursSunday || AGENCY_CONFIG.workingHours.sunday}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Simulated Google Map Frame with interactive markers */}
                <div className="bg-[#0F1E32] text-white rounded-2xl p-5 shadow-sm border border-slate-800 text-center flex flex-col items-center">
                  <Map className="w-10 h-10 text-[#C5A46E] mb-3" />
                  <h4 className="font-bold text-sm uppercase">{content.contactsPage.mapHeading || 'Локация на Офиса'}</h4>
                  <p className="text-[11px] text-slate-300 mt-1">{content.contactsPage.mapNote || 'Офисът се намира в непосредствена близост до Окръжна болница.'}</p>
                  <div className="w-full bg-[#15253A] rounded-xl h-44 mt-4 relative overflow-hidden flex flex-col justify-center items-center border border-slate-700/50">
                    <span className="text-[10px] text-amber-500 font-mono tracking-widest uppercase">КАРТА НА РАЙОНА</span>
                    <span className="text-xs font-bold text-white mt-1">{content.contactsPage.address || AGENCY_CONFIG.address}</span>
                    <span className="text-[10px] text-gray-400">Варна, България</span>
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-ping absolute center mt-2"></div>
                    <div className="w-3.5 h-3.5 bg-red-600 rounded-full border-2 border-white absolute center mt-2"></div>
                  </div>
                </div>
              </div>

              {/* Right Side: Message form */}
              <div className="lg:col-span-8 bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-150 text-left">
                <h3 className="font-extrabold text-[#0F1E32] text-lg uppercase tracking-tight mb-2">{content.contactsPage.formTitle || 'Изпратете ни писмено съобщение'}</h3>
                <p className="text-xs text-slate-500 mb-6 font-light">{content.contactsPage.formSubtitle || 'Имате въпроси за покупка, продажба или искате да обсъдим оферта? Попълнете формата и наш експерт ще се свърже с Вас.'}</p>
                
                <form 
                  onSubmit={handleContactSubmit}
                  className="space-y-4 text-xs font-sans text-slate-700"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold text-slate-500 uppercase tracking-wide mb-1">Име и Фамилия *</label>
                      <input 
                        required 
                        type="text" 
                        placeholder="напр. Георги Георгиев" 
                        value={contactFormData.name}
                        onChange={(e) => setContactFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-500 uppercase tracking-wide mb-1">Телефонен номер *</label>
                      <input 
                        required 
                        type="tel" 
                        placeholder="напр. +359 8..." 
                        value={contactFormData.phone}
                        onChange={(e) => setContactFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold text-slate-500 uppercase tracking-wide mb-1">Имейл адрес</label>
                      <input 
                        type="email" 
                        placeholder="напр. georgi@abv.bg" 
                        value={contactFormData.email}
                        onChange={(e) => setContactFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-500 uppercase tracking-wide mb-1">Тип оферта</label>
                      <select 
                        value={contactFormData.serviceType}
                        onChange={(e) => setContactFormData(prev => ({ ...prev, serviceType: e.target.value }))}
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#C5A46E] appearance-none cursor-pointer"
                      >
                        <option value="buy">Търся да закупя имот</option>
                        <option value="sell">Продавам собствен имот</option>
                        <option value="rent">Търся под наем</option>
                        <option value="landlord">Отдавам под наем</option>
                        <option value="consult">Друга консултация</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-500 uppercase tracking-wide mb-1">Вашето запитване *</label>
                    <textarea 
                      required 
                      rows={4} 
                      placeholder="Напишете Вашето съобщение или подробности за имота..." 
                      value={contactFormData.message}
                      onChange={(e) => setContactFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full bg-slate-50 border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                    />
                  </div>

                  <button
                    id="btn-contact-submit"
                    type="submit"
                    disabled={isContactSubmitting}
                    className="w-full sm:w-auto px-8 py-3 bg-[#0F1E32] hover:bg-amber-600 text-white font-bold tracking-widest uppercase rounded-lg shadow transition flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                  >
                    {isContactSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Изпращане по имейл...</span>
                      </>
                    ) : (
                      <span>{content.contactsPage.formButtonText || 'Изпрати по Имейл 📨'}</span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* =============== ABOUT US (ЗА НАС) DEDICATED VIEW =============== */}
        {activeTab === 'about' && (
          <div className="animate-fade-in font-sans pb-20">
            {/* Header banner */}
            <div className="relative bg-[#F4F7FA] text-[#0F1E32] pt-38 pb-16 md:pt-44 md:pb-20 text-center px-4 overflow-hidden border-b border-slate-100 shadow-sm">
              {/* Background photo overlay */}
              <div className="absolute inset-0 z-0">
                <img 
                  src={content.aboutSection.image || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&auto=format&fit=crop&q=80"} 
                  alt={content.seo?.pages?.about?.imageAltHero || "За Варна Имотен Център"} 
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-[#F4F7FA]/90 backdrop-blur-[1px]"></div>
              </div>
              <div className="relative z-10">
                <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">{content.aboutSection.badge || 'ЗА АГЕНЦИЯТА'}</span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mt-1 uppercase tracking-tight text-[#0F1E32]">
                  {content.seo?.pages?.about?.h1 || content.aboutSection.title || 'Нашата история и мисия'}
                </h1>
                <h2 className="text-sm text-slate-600 mt-2 max-w-lg mx-auto font-medium">
                  {content.seo?.pages?.about?.h2 || 'Вашият доверен партньор за покупка, продажба и наем на недвижими имоти в град Варна и региона.'}
                </h2>
                {content.seo?.pages?.about?.pageDescription && content.seo.pages.about.pageDescription !== (content.seo?.pages?.about?.h2 || 'Вашият доверен партньор за покупка, продажба и наем на недвижими имоти в град Варна и региона.') && (
                  <p className="text-xs text-slate-500 mt-2 max-w-xl mx-auto font-light leading-relaxed">
                    {content.seo.pages.about.pageDescription}
                  </p>
                )}
              </div>
            </div>

            {/* Main Content Section */}
            <div className="max-w-6xl mx-auto px-4 mt-12 md:mt-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                {/* Left: Image with golden border */}
                <div className="lg:col-span-5 relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                    <img 
                      src={content.aboutSection.image || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop&q=80"} 
                      alt="Варна Имотен Център Екип" 
                      className="w-full h-96 object-cover object-center hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute -bottom-5 -right-5 bg-[#C5A46E] text-[#0F1E32] p-4 rounded-xl shadow-xl font-bold text-xs uppercase tracking-wider hidden sm:block border-2 border-white">
                    Лицензирана Агенция
                  </div>
                </div>

                {/* Right: Description & Philosophy */}
                <div className="lg:col-span-7 space-y-5 text-left">
                  <div>
                    <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">
                      Кои сме ние
                    </span>
                    <h2 className="text-2xl md:text-3xl font-black text-[#0F1E32] uppercase tracking-tight mt-1 leading-snug">
                      Професионализъм, сигурност и персонално отношение
                    </h2>
                  </div>
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed font-light">
                    {content.aboutSection.paragraph1}
                  </p>
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed font-light">
                    {content.aboutSection.paragraph2}
                  </p>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div className="text-2xl font-black text-[#0F1E32]">15+</div>
                      <div className="text-xs text-slate-500 mt-1 font-medium">Години опит на варненския пазар</div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div className="text-2xl font-black text-[#0F1E32]">100%</div>
                      <div className="text-xs text-slate-500 mt-1 font-medium">Правна сигурност и чистота на сделките</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3.5 pt-4">
                    <button
                      type="button"
                      onClick={() => handleTabChange('team')}
                      className="px-5 py-2.5 bg-[#0F1E32] hover:bg-[#1a2f4c] text-white rounded-xl text-xs font-bold uppercase tracking-wider transition shadow cursor-pointer"
                    >
                      Запознайте се с екипа &rarr;
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTabChange('contacts')}
                      className="px-5 py-2.5 bg-[#C5A46E] hover:bg-[#b08e58] text-white rounded-xl text-xs font-bold uppercase tracking-wider transition shadow cursor-pointer"
                    >
                      Свържете се с нас 📨
                    </button>
                  </div>
                </div>
              </div>

              {/* 4 Core Values */}
              <div className="mt-20 pt-12 border-t border-slate-200">
                <div className="text-center mb-10">
                  <span className="text-xs text-[#8C6221] font-bold tracking-widest uppercase">НАШИТЕ ЦЕННОСТИ</span>
                  <h3 className="text-2xl md:text-3xl font-black text-[#0F1E32] uppercase mt-1">Защо да изберете нас?</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-left">
                    <h4 className="font-bold text-[#0F1E32] text-sm uppercase">Персонално отношение</h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Всеки клиент има персонален брокер, който го води през всеки етап — от първия оглед до нотариуса.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-left">
                    <h4 className="font-bold text-[#0F1E32] text-sm uppercase">100% Проверени оферти</h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Всички обяви преминават проверка на документи, вещни тежести и реална собственост преди публикуване.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-left">
                    <h4 className="font-bold text-[#0F1E32] text-sm uppercase">Пълно правно съдействие</h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Изготвяне на договори, проверки в имотния регистър, данъчни оценки и представителство пред нотариус.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-left">
                    <h4 className="font-bold text-[#0F1E32] text-sm uppercase">Локална експертиза</h4>
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                      Познаваме детайлно всеки квартал във Варна, ценовите тенденции, инфраструктурата и перспективите.
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Reviews Screenshot Carousel */}
              <div className="mt-16">
                <GoogleReviewsCarousel 
                  screenshots={content.aboutSection.googleReviewsScreenshots}
                  title={content.aboutSection.reviewsTitle}
                  subtitle={content.aboutSection.reviewsSubtitle}
                  googleReviewsUrl={content.aboutSection.googleReviewsUrl}
                  rating={content.aboutSection.reviewsRating}
                  ratingText={content.aboutSection.reviewsRatingText}
                  isAdmin={user !== null}
                  onOpenAdminReviews={() => {
                    setActiveTab('admin');
                  }}
                />
              </div>
            </div>
          </div>
        )}


        {/* =============== LEGAL PAGES (ОБЩИ УСЛОВИЯ И ПОВЕРИТЕЛНОСТ / GDPR) =============== */}
        {(activeTab === 'terms' || activeTab === 'privacy') && (
          <LegalPage
            initialDocument={activeTab === 'privacy' ? 'privacy' : 'terms'}
            onNavigateHome={() => handleTabChange('home')}
            onNavigateTab={handleTabChange}
          />
        )}

        {/* =============== ADMIN DASHBOARD PANEL =============== */}
        {activeTab === 'admin' && (
          <div className="animate-fade-in font-sans">
            {authLoading ? (
              <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-500 py-24">
                <Loader2 className="w-8 h-8 animate-spin text-[#C5A46E]" />
                <p className="text-xs uppercase tracking-wider font-semibold">Проверка на администраторски достъп...</p>
              </div>
            ) : user ? (
              <AdminPanel 
                apartments={apartments}
                onAddProperty={handleAddProperty}
                onEditProperty={handleEditProperty}
                onDeleteProperty={handleDeleteProperty}
                onResetProperties={handleResetProperties}
                onReorderProperties={handleReorderProperties}
                onToggleStatus={handleTogglePropertyStatus}
                neighborhoods={uniqueNeighborhoods}
                agents={agents}
                onAddAgent={handleAddAgent}
                onEditAgent={handleEditAgent}
                onDeleteAgent={handleDeleteAgent}
                onResetAgents={handleResetAgents}
                onReorderAgents={handleReorderAgents}
                onExitAdmin={() => handleTabChange('home')}
                onRefreshProperties={fetchServerProperties}
              />
            ) : (
              <AdminLogin onBackToSite={() => handleTabChange('home')} />
            )}
          </div>
        )}

      </main>

      {/* FOOTER */}
      {activeTab !== 'admin' && (
        <Footer 
          onNavigate={(tab) => {
            handleTabChange(tab);
          }} 
          onSelectNeighborhood={handleSelectPopularNeighborhood}
        />
      )}

      {/* ================= EXTRA MODAL INTRAFACES ================= */}

      {/* 1. Property Detail Modal popup triggers */}
      {selectedApartment && (
        <PropertyDetailModal 
          apartment={selectedApartment}
          isOpen={!!selectedApartment}
          onClose={handleCloseApartment}
          isFavorite={favorites.includes(selectedApartment.id)}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* 2. Free Consultation Intake Modal - Page 1 */}
      {isConsultationOpen && (
        <div id="consultation-modal" className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm flex justify-center items-center z-55 p-4 overflow-y-auto font-sans text-xs text-slate-700">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-100 relative animate-fade-in">
            <button 
              onClick={() => setIsConsultationOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-800 focus:outline-none font-bold text-sm"
            >
              ✕
            </button>
            <div className="bg-[#0F1E32] p-5 text-white text-left">
              <span className="text-[#C5A46E] font-bold uppercase tracking-wider text-[10px]">Коректно отношение</span>
              <h3 className="text-lg font-black uppercase mt-0.5">Безплатна консултация</h3>
              <p className="text-[11px] text-slate-300 font-light mt-1">Оставете координатите си. Наш консултант ще се свърже с Вас възможно най-скоро.</p>
            </div>
            
            <form onSubmit={handleConsultationSubmit} className="p-6 space-y-4 text-left">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">Вашето име *</label>
                <input 
                  required 
                  type="text" 
                  placeholder="напр. Иванна Стоянова" 
                  value={consultationFormData.name}
                  onChange={(e) => setConsultationFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-slate-50 border border-gray-200 rounded-lg p-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">Имейл адрес (за писмен отговор и оферти)</label>
                <input 
                  type="email" 
                  placeholder="вашият-имейл@gmail.com" 
                  value={consultationFormData.email}
                  onChange={(e) => setConsultationFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-slate-50 border border-gray-200 rounded-lg p-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">Телефонен номер *</label>
                  <input 
                    required 
                    type="tel" 
                    placeholder="+359 ..." 
                    value={consultationFormData.phone}
                    onChange={(e) => setConsultationFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full bg-slate-50 border border-gray-200 rounded-lg p-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#C5A46E]" 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">Свободен час</label>
                  <select 
                    value={consultationFormData.preferredTime}
                    onChange={(e) => setConsultationFormData(prev => ({ ...prev, preferredTime: e.target.value }))}
                    className="w-full bg-slate-50 border border-gray-200 rounded-lg p-2 text-xs focus:outline-none cursor-pointer"
                  >
                    <option value="now">Възможно най-рано</option>
                    <option value="morning">Сутрин (9:00 - 12:00)</option>
                    <option value="afternoon">Обедно време (12:00 - 14:00)</option>
                    <option value="evening">След обяд (14:00 - 18:00)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">Област на интерес</label>
                <select 
                  value={consultationFormData.interest}
                  onChange={(e) => setConsultationFormData(prev => ({ ...prev, interest: e.target.value }))}
                  className="w-full bg-slate-50 border border-gray-200 rounded-lg p-2 text-xs focus:outline-none cursor-pointer"
                >
                  <option value="buy">Покупка на апартамент</option>
                  <option value="sell">Продажба на собствен имот</option>
                  <option value="rent">Дългосрочен наем / Наемане</option>
                  <option value="landlord">Отдаване под наем</option>
                  <option value="credit">Ипотечен кредит / Оценка</option>
                </select>
              </div>

              <button 
                type="submit"
                disabled={isConsultationSubmitting}
                className="w-full bg-[#C5A46E] hover:bg-[#B1905E] text-white font-bold py-2.5 rounded-lg uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {isConsultationSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Изпращане по имейл...</span>
                  </>
                ) : (
                  <span>ЗАЯВИ СЪДЕЙСТВИЕ ПО ИМЕЙЛ ✨</span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 3. "Нашата история и мисия" Read More Popup popup */}
      {isAboutModalOpen && (
        <div id="about-readmore" className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm flex justify-center items-center z-55 p-4 overflow-y-auto font-sans text-xs text-slate-700">
          <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl relative animate-fade-in p-6 md:p-8 text-left">
            <button 
              onClick={() => setIsAboutModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-[#0F1E32] font-bold text-base focus:outline-none"
            >
              ✕
            </button>
            <span className="text-[#C5A46E] font-bold uppercase tracking-widest text-[10px]">Кои сме ние?</span>
            <h3 className="text-xl font-black uppercase text-[#0F1E32] mt-0.5">Варна Имотен Център</h3>
            
            <div className="mt-4 space-y-3.5 leading-relaxed text-slate-600 font-light text-xs">
              <p>
                Нашата основна концепция е създаването на сигурен мост между продавачите / наемодателите и крайните купувачи / наематели в морската столица на България — Варна.
              </p>
              <p>
                Изповядването на сделка с недвижим имот е едно от най-вълнуващите и финансово отговорни събития в живота на всяко семейство. Затова подхождаме с максимално ниво на правен одит, извличайки само сертифицирани оферти с доказани собственици без тежести.
              </p>
              <p className="font-semibold text-slate-800">
                Защо клиентите ни се доверяват безрезервно?
              </p>
              <ul className="space-y-1.5 pl-1.5 text-slate-700">
                <li>• <b>Опит на местния пазар</b> — знаем в кой квартал коя е точната разпределителна цена за тухла или панел.</li>
                <li>• <b>Коректност и прозрачност</b> — без укрити комисиони, измислени поддържащи такси или дублирани реклами.</li>
                <li>• <b>Практически решения</b> — партнираме си с най-добрите архитекти, юристи и нотариуси във Варна за супер прецизност.</li>
              </ul>
              <p className="pt-2 border-t border-gray-100 text-[11px] text-[#C5A46E] font-medium">
                Нашата мисия: Вашият нов спокоен дом във Варна!
              </p>
            </div>
            
            <button
              onClick={() => setIsAboutModalOpen(false)}
              className="mt-6 w-full bg-[#0F1E32] text-white font-bold py-2.5 rounded-lg text-xs uppercase cursor-pointer"
            >
              Разбрах
            </button>
          </div>
        </div>
      )}

      {/* 4. European GDPR Cookie Consent & Settings */}
      <CookieConsentBanner />
      {activeTab !== 'admin' && <CookieSettingsTrigger />}

    </div>
  );
}
